"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useEcomFeedback, EcomButton, EcomDropdown } from "../../ui";
import { ecomImageApi, TASK_STATUS, type BatchTaskChild, type GenerateTask } from "../../lib/ecom-image-api";
import { pickEcomResultSlotImage } from "../../lib/ecom-result-image-source";
import InspirationUploadModal from "./InspirationUploadModal";
import type { CreatePostRequest } from "../../lib/inspiration-api";
import { useHistoryFavorite } from "./useHistoryFavorite";
import { Heart } from "lucide-react";
import {
  downloadBlob,
  downloadFile,
  isDownloadCancelError,
} from "../../lib/download";
import type { EcomActiveHistoryTaskPayload, EcomHistoryRegeneratePayload, HistoryGroupDescriptor } from "../../lib/ecom-history-selection";
import {
  chooseHistoryDownloadMode,
  collectHistoryDownloadUrls,
  resolveHistoryDownloadImageCount,
  type HistoryDownloadGroupLike,
} from "../../lib/ecom-history-download";
import { extractPromptFromParams, extractPromptValue, resolveTaskOutputImages } from "../../lib/ecom-image-history-utils";
import { inferHistoryStatusFromCounts, PARTIAL_HISTORY_STATUS, projectAgentHistoryTask, resolveHistoryDisplayStatus, shouldCreateSyntheticCurrentTask, shouldKeepCurrentTaskOverlay } from "../../lib/ecom-history-status";
import { nextHistoryPage } from "../../lib/ecom-history-pagination";
import { collectHistoryThumbnailStack } from "../../lib/ecom-history-thumbnail-stack";
import { fetchHistoryImageContentLength } from "../../lib/ecom-history-image-meta";
import HistoryRecordModal from "./HistoryRecordModal";
import SafeImage from "./SafeImage";
import { getImageLayerTask, type ImageLayerTask } from "../../lib/image-layer-api";
import { startEcomPollingLoop } from "../../lib/ecom-polling";
import { useEcomPollingFeedback } from "../../ui";
import type { AgentConversation } from "../../agent/http-client";
import type { AgentHostController } from "../../agent/host-controller";
import {
  agentHistoryModuleKeys,
  hasMoreAgentHistory,
  loadAgentHistoryPages,
  loadNextAgentHistoryPage,
  type AgentHistoryCursors,
  type AgentHistoryPageReader,
} from "../../agent/history-modules";
import { AgentHistoryCard } from "../agent/AgentHistoryCard";

const IMAGE_LAYER_HISTORY_SYNC_EVENT = "flowx:ecom-image-layer-history-sync";
const THUMB_FAIL_SEP = "\x1e";

function thumbFailMapKey(slotId: string, url: string) {
  return `${slotId}${THUMB_FAIL_SEP}${url}`;
}

function DouziAmount({
  amount,
  className = "",
}: {
  amount: number;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-0.5 text-[#00FAE5] ${className}`}>
      <span className="h-3.5 w-3.5 shrink-0 bg-[url('/douzi.svg')] bg-contain bg-center bg-no-repeat" aria-hidden />
      <span className="text-white">{amount}</span>
    </span>
  );
}

interface WorkflowOption {
  feature_key: string;
  name: string;
}

// 分组后的历史记录项
interface HistoryGroup {
  groupId: string;           // group_id，空字符串表示独立任务
  tasks: GenerateTask[];     // 组内所有任务
  thumbnail: string;         // 第一张缩略图
  featureKey: string;        // 工作流类型
  prompt: string;            // 提示词
  status: number;            // 聚合状态
  progress: number;          // 聚合进度
  createdAt: string;         // 创建时间
  imageCount: number;        // 图片总数
  isBatchParent?: boolean;   // 是否为批处理父任务
  isBatchGroup?: boolean;    // 是否为按 batch_index 聚合的批处理组
  batchTotal?: number;       // 批处理总数
  batchCompletedCount?: number; // 已完成子任务数
  batchFailedCount?: number;    // 失败子任务数
}

interface HistoryPanelProps {
  variant?: "panel" | "page";
  className?: string;
  activeTab?: string;
  onImageSelect?: (imageUrl: string) => void;
  onTaskCompleted?: (result: {
    effectImage: string;
    modelImage: string;
    productImage: string;
    taskId: string;
    featureKey?: string;
  }) => void;
  onGroupSelect?: (result: {
    images: string[];
    modelImage: string;
    productImage: string;
    featureKey?: string;
    childTaskIds?: string[];
    childStatuses?: number[];
    taskId?: string;
  }) => void;
  refreshTrigger?: number;
  teamId?: number;
  workflowOptions?: WorkflowOption[];
  currentTaskIds?: string[];
  currentProgress?: number;
  taskProgresses?: number[];
  currentGeneratedImages?: string[];
  currentFailedIndexes?: number[];
  currentStatus?: "empty" | "generating" | "completed" | "failed";
  currentInspireResults?: Array<{
    taskId: string;
    status?: "pending" | "queued" | "processing" | "completed" | "failed" | "cancelled";
    prompt?: string;
    presetLabel?: string;
    inputImages?: string[];
    batchIndex?: number;
    templateId?: number;
  }>;
  onActiveTaskClick?: (payload?: EcomActiveHistoryTaskPayload) => void;
  onRegenerateFromHistory?: (payload: EcomHistoryRegeneratePayload) => void;
  onOpenAgentHistory?: (taskId: string) => Promise<void>;
  agentController?: AgentHostController;
  onOpenAgentConversation?: (conversationId: string) => Promise<void>;
  agentFeatureKeys?: string[];
  restoringAgentTaskId?: string | null;
  onExpand?: () => void;
  onCollapse?: () => void;
  regeneratingTaskUuids?: string[];
  refiningTaskUuids?: string[];
  /** 为 true 时保留失败任务（右侧抽屉使用）；默认 false 与整页历史行为一致 */
  includeFailed?: boolean;
  /** 外部受控的类型筛选 key（用于跨实例共享状态） */
  filterKey?: string;
  /** 类型筛选变更回调 */
  onFilterChange?: (key: string) => void;
  /** 打开已完成的文字分离结果。 */
  onViewImageLayer?: (task: ImageLayerTask, imageUrl: string) => void;
  /** 当前标签卡对应的工作流 key；筛选为「当前工作流」时使用 */
  workflowKey?: string;
}

const PARTIAL_STATUS = PARTIAL_HISTORY_STATUS;

const STATUS_CONFIG: Record<number, { label: string; color: string; bg: string }> = {
  [TASK_STATUS.PENDING]: { label: "等待中", color: "text-white/50", bg: "bg-white/10" },
  [TASK_STATUS.PROCESSING]: { label: "生成中", color: "text-blue-400", bg: "bg-blue-500/15" },
  [TASK_STATUS.COMPLETED]: { label: "已完成", color: "text-emerald-400", bg: "bg-emerald-500/15" },
  [TASK_STATUS.FAILED]: { label: "失败", color: "text-red-400", bg: "bg-red-500/15" },
  [TASK_STATUS.CANCELLED]: { label: "已取消", color: "text-white/40", bg: "bg-white/8" },
  [TASK_STATUS.QUEUED]: { label: "排队中", color: "text-white/40", bg: "bg-white/5" },
  [PARTIAL_STATUS]: { label: "部分完成", color: "text-amber-400", bg: "bg-amber-500/15" },
};

const TEXT_SEPARATION_RECORD_RE = /文字(?:分离|识别|分层)|移除文字|remove[_-]?text|text[_-]?(?:only|inpaint|recognition|layer)/i;

function isActiveTaskStatus(status: number): boolean {
  return status === TASK_STATUS.PENDING || status === TASK_STATUS.PROCESSING || status === TASK_STATUS.QUEUED;
}

function mapInspireStatusToTaskStatus(status?: string): number {
  switch (status) {
    case "completed":
      return TASK_STATUS.COMPLETED;
    case "failed":
      return TASK_STATUS.FAILED;
    case "cancelled":
      return TASK_STATUS.CANCELLED;
    case "queued":
      return TASK_STATUS.QUEUED;
    case "processing":
      return TASK_STATUS.PROCESSING;
    case "pending":
    default:
      return TASK_STATUS.PENDING;
  }
}

function progressForStatus(status: number, progress: number | undefined): number {
  if (status === TASK_STATUS.COMPLETED || status === TASK_STATUS.FAILED) return 100;
  if (isActiveTaskStatus(status)) return progress || 0;
  return progress ?? 0;
}

function mapImageLayerTaskStatus(status: ImageLayerTask["status"]): number {
  switch (status) {
    case "pending":
      return TASK_STATUS.PENDING;
    case "processing":
      return TASK_STATUS.PROCESSING;
    case "succeeded":
    case "completed":
      return TASK_STATUS.COMPLETED;
    case "failed":
      return TASK_STATUS.FAILED;
    case "cancelled":
      return TASK_STATUS.CANCELLED;
    default:
      return TASK_STATUS.PENDING;
  }
}

function getImageLayerTaskOutput(task: ImageLayerTask): string {
  if (task.clean_background_url?.trim()) return task.clean_background_url.trim();
  const backgroundLayer = (task.layers || []).find((layer) => layer.type === "background" && layer.image_url?.trim());
  return backgroundLayer?.image_url?.trim() || "";
}

function imageLayerTaskToHistoryTask(task: ImageLayerTask, fallbackImageUrl = ""): GenerateTask {
  const status = mapImageLayerTaskStatus(task.status);
  const sourceImage = (task.source_image_url || task.image?.url || fallbackImageUrl || "").trim();
  const outputImage = getImageLayerTaskOutput(task);
  const isDone = status === TASK_STATUS.COMPLETED || status === TASK_STATUS.FAILED || status === TASK_STATUS.CANCELLED;

  return {
    uuid: task.task_uuid,
    group_id: "",
    task_type: task.task_kind || "image_layer",
    execution_kind: task.task_kind === "seedream_layer" ? "postprocess" : "text_separation",
    feature_key: task.task_kind || "image_layer",
    title: task.task_kind === "seedream_layer" ? "文字分离" : "文字分层",
    thumbnail: outputImage || sourceImage,
    input_images: sourceImage ? [sourceImage] : [],
    image_count: 1,
    status,
    progress: status === TASK_STATUS.COMPLETED || status === TASK_STATUS.FAILED ? 100 : 5,
    points_cost: 0,
    prompt: task.task_kind === "seedream_layer" ? "文字分离" : "文字分层",
    created_at: task.created_at || new Date().toISOString(),
    completed_at: isDone ? (task.updated_at || new Date().toISOString()) : "",
  };
}

function normalizeHistoryMediaKey(value?: string): string {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  const queryIndex = trimmed.search(/[?#]/);
  return queryIndex >= 0 ? trimmed.slice(0, queryIndex) : trimmed;
}

function isTextSeparationTaskRecord(task: Pick<GenerateTask, "title" | "feature_key" | "task_type" | "execution_kind" | "prompt">): boolean {
  const fields = [
    task.title,
    task.feature_key,
    task.task_type,
    task.execution_kind,
    task.prompt,
  ]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join(" ");

  return TEXT_SEPARATION_RECORD_RE.test(fields);
}

function getTextSeparationSourceKey(task: Pick<GenerateTask, "input_images">): string {
  const sourceImage = task.input_images?.find((image) => image.trim().length > 0);
  return normalizeHistoryMediaKey(sourceImage);
}

function hasTextSeparationOutput(task: GenerateTask): boolean {
  const thumbKey = normalizeHistoryMediaKey(task.thumbnail);
  if (!thumbKey) return false;
  const sourceKey = getTextSeparationSourceKey(task);
  return !sourceKey || thumbKey !== sourceKey;
}

function getImageLayerDetailScore(detail?: ImageLayerTask): number {
  if (!detail) return 0;
  let score = 0;
  if (detail.status === "completed" || detail.status === "succeeded") score += 20;
  if (detail.clean_background_url) score += 20;
  if ((detail.layers || []).length > 0) score += 10;
  return score;
}

function getTextSeparationDisplayScore(
  task: GenerateTask,
  syncedIds: Set<string>,
  imageLayerTaskDetails: Record<string, ImageLayerTask>,
): number {
  let score = 0;
  if (task.status === TASK_STATUS.COMPLETED) score += 100;
  else if (task.status === TASK_STATUS.PROCESSING) score += 50;
  else if (task.status === TASK_STATUS.PENDING || task.status === TASK_STATUS.QUEUED) score += 20;
  else if (task.status === TASK_STATUS.FAILED) score -= 20;
  if (hasTextSeparationOutput(task)) score += 40;
  if (!syncedIds.has(task.uuid)) score += 8;
  score += getImageLayerDetailScore(imageLayerTaskDetails[task.uuid]);
  return score;
}

function dedupeTextSeparationHistory(
  items: GenerateTask[],
  syncedIds: Set<string>,
  imageLayerTaskDetails: Record<string, ImageLayerTask>,
): GenerateTask[] {
  const bestBySource = new Map<string, GenerateTask>();

  for (const item of items) {
    if (!isTextSeparationTaskRecord(item)) continue;
    const sourceKey = getTextSeparationSourceKey(item);
    if (!sourceKey) continue;
    const current = bestBySource.get(sourceKey);
    if (
      !current ||
      getTextSeparationDisplayScore(item, syncedIds, imageLayerTaskDetails) > getTextSeparationDisplayScore(current, syncedIds, imageLayerTaskDetails)
    ) {
      bestBySource.set(sourceKey, item);
    }
  }

  if (bestBySource.size === 0) return items;
  return items.filter((item) => {
    if (!isTextSeparationTaskRecord(item)) return true;
    const sourceKey = getTextSeparationSourceKey(item);
    if (!sourceKey) return true;
    return bestBySource.get(sourceKey)?.uuid === item.uuid;
  });
}

function formatTime(dateStr: string): string {
  if (!dateStr) return "";
  try {
    const match = dateStr.match(/^\d{4}-(\d{2}-\d{2})\s+(\d{2}:\d{2})/);
    if (match) return `${match[1]} ${match[2]}`;
    const d = new Date(dateStr.includes("+") || dateStr.includes("Z") ? dateStr : dateStr + "+08:00");
    return d.toLocaleString("zh-CN", {
      timeZone: "Asia/Shanghai",
      month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatFullTime(dateStr: string): string {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr.includes("+") || dateStr.includes("Z") ? dateStr : dateStr + "+08:00");
    return d.toLocaleString("zh-CN", {
      timeZone: "Asia/Shanghai",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function parseHistoryDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const hyphenated = trimmed.replace(/\//g, "-");
  const normalized = hyphenated.includes("T") ? hyphenated : hyphenated.replace(" ", "T");
  const parsed = new Date(hasTimezone ? normalized : `${normalized}+08:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getShanghaiParts(date: Date) {
  const parts = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const valueOf = (type: string) => parts.find((part) => part.type === type)?.value || "";
  return {
    year: valueOf("year"),
    month: valueOf("month"),
    day: valueOf("day"),
    hour: valueOf("hour"),
    minute: valueOf("minute"),
  };
}

function getHistoryDateKey(dateStr: string): string {
  const parsed = parseHistoryDate(dateStr);
  if (!parsed) return "unknown";
  const parts = getShanghaiParts(parsed);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function getHistoryTimestamp(dateStr: string): number {
  return parseHistoryDate(dateStr)?.getTime() || 0;
}

function formatHistoryDateLabel(dateStr: string): string {
  const parsed = parseHistoryDate(dateStr);
  if (!parsed) return "未知日期";

  const current = getShanghaiParts(parsed);
  const todayKey = getHistoryDateKey(new Date().toISOString());
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = getHistoryDateKey(yesterday.toISOString());
  const currentYear = getShanghaiParts(new Date()).year;
  const key = `${current.year}-${current.month}-${current.day}`;

  if (key === todayKey) return "今天";
  if (key === yesterdayKey) return "昨天";
  if (current.year === currentYear) {
    return `${Number(current.month)}月${Number(current.day)}日`;
  }
  return `${current.year}年${Number(current.month)}月${Number(current.day)}日`;
}

function formatHistoryRecordTime(dateStr: string): string {
  const parsed = parseHistoryDate(dateStr);
  if (!parsed) return "";
  const parts = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(parsed);
  const valueOf = (type: string) => parts.find((part) => part.type === type)?.value || "";
  return `${valueOf("year")}/${valueOf("month")}/${valueOf("day")} ${valueOf("hour")}:${valueOf("minute")}:${valueOf("second")}`;
}

function formatDimensionLabel(dimensions?: string): string {
  if (!dimensions) return "";
  const normalized = dimensions.replace(/×/g, " x ").trim();
  return normalized.toLowerCase().endsWith("px") ? normalized : `${normalized} px`;
}

function HistoryMetaField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex shrink-0 items-center gap-0.5 text-[12px] leading-none text-[#E6E6E6]">
      <span className="text-[#E6E6E6]/55">{label}：</span>
      <span>{children}</span>
    </span>
  );
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const precision = unitIndex === 0 || value >= 10 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
}

function HistoryRegenerateIcon({
  spinning = false,
  sizeClass = "h-4 w-4",
}: {
  spinning?: boolean;
  sizeClass?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      className={`${sizeClass} ${spinning ? "animate-spin" : ""}`}
      viewBox="0 0 1024 1024"
      fill="currentColor"
    >
      <path d="M952.529455 459.752727a34.909091 34.909091 0 0 0-49.361455 0l-21.085091 21.085091C860.625455 276.317091 687.220364 116.363636 477.090909 116.363636a400.570182 400.570182 0 0 0-48.686545 3.211637c-7.842909 0.930909-15.616 2.164364-23.342546 3.560727-4.701091 0.837818-9.402182 1.582545-14.010182 2.56-9.844364 2.164364-19.572364 4.654545-29.160727 7.540364-6.865455 2.024727-13.521455 4.398545-20.247273 6.749091-3.537455 1.28-7.121455 2.56-10.635636 3.93309-7.237818 2.792727-14.336 5.701818-21.364364 8.890182-1.489455 0.674909-2.978909 1.442909-4.468363 2.141091-15.592727 7.330909-30.650182 15.499636-45.079273 24.645818l-1.931636 1.163637a394.309818 394.309818 0 0 0-26.624 18.734545 411.322182 411.322182 0 0 0-54.551273 49.780364c-2.443636 2.653091-4.887273 5.259636-7.261091 7.982545A375.04 375.04 0 0 0 148.945455 283.461818a407.319273 407.319273 0 0 0-37.771637 62.557091c-1.000727 2.071273-2.141091 4.026182-3.118545 6.097455-0.232727 0.418909-0.279273 0.861091-0.465455 1.28A404.340364 404.340364 0 0 0 69.818182 523.636364C69.818182 748.218182 252.509091 930.909091 477.090909 930.909091c143.476364 0 273.687273-73.309091 348.299636-196.049455a34.909091 34.909091 0 0 0-59.671272-36.258909C703.883636 800.349091 595.991273 861.090909 477.090909 861.090909 291.025455 861.090909 139.636364 709.701818 139.636364 523.636364a335.127273 335.127273 0 0 1 32.907636-144.384A335.755636 335.755636 0 0 1 210.385455 317.672727c4.119273-5.352727 8.378182-10.565818 12.8-15.616l3.397818-3.746909a341.224727 341.224727 0 0 1 48.151272-44.101818l3.258182-2.443636a340.619636 340.619636 0 0 1 58.112-34.327273l1.908364-0.930909a313.320727 313.320727 0 0 1 26.391273-10.565818c5.771636-2.094545 11.52-4.096 17.408-5.841455 7.842909-2.327273 15.802182-4.421818 23.901091-6.167273 3.909818-0.861091 7.912727-1.466182 11.845818-2.187636a371.479273 371.479273 0 0 1 29.416727-4.049455c9.937455-0.930909 19.968-1.536 30.114909-1.536 169.378909 0 309.597091 125.556364 333.474909 288.395637l-14.801454-14.824727a34.909091 34.909091 0 1 0-49.361455 49.338181l78.382546 78.429091a34.932364 34.932364 0 0 0 49.338181 0l78.405819-78.429091a34.909091 34.909091 0 0 0 0-49.338181" />
    </svg>
  );
}

function HistoryDownloadIcon({
  spinning = false,
  sizeClass = "h-4 w-4",
}: {
  spinning?: boolean;
  sizeClass?: string;
}) {
  if (spinning) {
    return <span className={`${sizeClass} rounded-full border border-current/30 border-t-current animate-spin`} aria-hidden />;
  }
  return (
    <svg
      aria-hidden="true"
      className={sizeClass}
      viewBox="0 0 16 16"
      fill="none"
    >
      <path d="M8 2.5v7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M5.25 7.25 8 10l2.75-2.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 11.5v1A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CopyPromptButton({
  copied,
  onClick,
  className = "",
  size = "sm",
}: {
  copied: boolean;
  onClick: (e: React.MouseEvent) => void;
  className?: string;
  size?: "xs" | "sm";
}) {
  const iconClass = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";
  const buttonSize = size === "xs" ? "w-5 h-5" : "w-6 h-6";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${buttonSize} flex items-center justify-center rounded transition-all ${copied
        ? "bg-emerald-500/20 text-emerald-400"
        : "bg-white/8 hover:bg-white/15 text-white/40 hover:text-white/70"
        } ${className}`}
      title={copied ? "已复制" : "复制提示词"}
      aria-label={copied ? "已复制提示词" : "复制提示词"}
    >
      {copied ? (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

function SubTaskPromptPreview({
  prompt,
  copied,
  onCopy,
}: {
  prompt: string;
  copied: boolean;
  onCopy: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      className="mt-1 flex items-center gap-1 rounded-md bg-white/4 px-1.5 py-1"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="min-w-0 flex-1 truncate text-[10px] text-white/45"
        title={prompt}
      >
        {prompt}
      </div>
      <CopyPromptButton copied={copied} onClick={onCopy} size="xs" />
    </div>
  );
}

interface ImagePreviewModalProps {
  task: GenerateTask;
  groupId?: string;
  onClose: () => void;
  workflowOptions?: WorkflowOption[];
}

function ImagePreviewModal({ task, groupId, onClose, workflowOptions = [] }: ImagePreviewModalProps) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const targetScaleRef = useRef(1);
  const scaleAnimRef = useRef<number | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const [imageFileSize, setImageFileSize] = useState<string | null>(null);
  const [imageFileSizeBytes, setImageFileSizeBytes] = useState<number>(0);
  const [singleImageUrl, setFullImageUrl] = useState<string>(task.thumbnail || "");
  const [actionMsg, setActionMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [detailLoaded, setDetailLoaded] = useState(false);
  const [published, setPublished] = useState(false);
  const [taskParams, setTaskParams] = useState<Record<string, unknown> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [galleryTaskUUIDs, setGalleryTaskUUIDs] = useState<string[]>([]);
  const [galleryCompletedTimes, setGalleryCompletedTimes] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [groupPointsCost, setGroupPointsCost] = useState(0);
  const [groupTaskCount, setGroupTaskCount] = useState(0);
  const isGroupMode = !!groupId;
  // Derive both the visible URL and its task identity from this render's slot.
  const fullImageUrl = isGroupMode ? galleryImages[currentIndex] || singleImageUrl : singleImageUrl;

  const promptText = extractPromptFromParams(taskParams) || extractPromptValue(task.prompt || "");
  const favoriteTaskUUID = detailLoaded ? (isGroupMode ? galleryTaskUUIDs[currentIndex] || "" : task.uuid) : "";
  const { isFavorited, saving, checking: checkingFavorite, failed: favoriteLookupFailed, toggle: toggleFavorite } = useHistoryFavorite({
    taskUUID: favoriteTaskUUID,
    mediaURL: fullImageUrl,
    createPost: (meta): CreatePostRequest => ({
      title: task.title || "AI生成图片",
      description: promptText,
      media_url: fullImageUrl,
      media_type: "image",
      category: "其他",
      tags: [],
      prompt: promptText,
      source_type: "generated",
      source_task_uuid: favoriteTaskUUID,
      visibility: "private",
      width: meta.width,
      height: meta.height,
      file_size: imageFileSizeBytes || undefined,
    }),
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !publishing) onClose();
      if (isGroupMode && galleryImages.length > 1) {
        if (e.key === "ArrowLeft") setCurrentIndex(i => Math.max(0, i - 1));
        if (e.key === "ArrowRight") setCurrentIndex(i => Math.min(galleryImages.length - 1, i + 1));
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, isGroupMode, galleryImages.length]);

  useEffect(() => {
    if (!isGroupMode) {
      let cancelled = false;
      (async () => {
        try {
          const detail = await ecomImageApi.getTask(task.uuid);
          if (cancelled) return;
          if (detail.data?.params) {
            setTaskParams(detail.data.params as Record<string, unknown>);
          }
          const outputImages = resolveTaskOutputImages(
            detail.data?.output_images,
            detail.data?.coze_raw_output,
          );
          if (outputImages && outputImages.length > 0) {
            setFullImageUrl(outputImages[0]);
          }
        } catch { /* keep thumbnail */ }
        finally { if (!cancelled) setDetailLoaded(true); }
      })();
      return () => { cancelled = true; };
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await ecomImageApi.getTaskGroup(groupId);
        if (cancelled || !res.data) return;
        const { tasks: childTasks, points_cost, prompt } = res.data;
        setGroupPointsCost(points_cost || 0);
        setGroupTaskCount(childTasks?.length || 0);
        if (prompt) {
          setTaskParams({ prompt });
        }

        const sorted = [...(childTasks || [])].sort(
          (a, b) => (a.batch_index || 0) - (b.batch_index || 0)
        );
        const images: string[] = [];
        const taskUUIDs: string[] = [];
        const completedTimes: string[] = [];
        for (const t of sorted) {
          const img = (t.output_images || [])[0];
          if (img && img.trim()) {
            images.push(img);
            taskUUIDs.push(t.uuid);
            completedTimes.push(t.completed_at || "");
          } else if (t.thumbnail && t.thumbnail.trim()) {
            images.push(t.thumbnail);
            taskUUIDs.push(t.uuid);
            completedTimes.push(t.completed_at || "");
          }
        }
        if (images.length > 0) {
          setGalleryImages(images);
          setGalleryTaskUUIDs(taskUUIDs);
          setGalleryCompletedTimes(completedTimes);
        }
      } catch { /* keep thumbnail */ }
      finally { if (!cancelled) setDetailLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [task.uuid, groupId, isGroupMode]);

  useEffect(() => {
    if (!fullImageUrl) return;
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = fullImageUrl;

    fetch(fullImageUrl, { method: "HEAD" })
      .then(res => {
        const len = res.headers.get("content-length");
        if (len) {
          const bytes = parseInt(len, 10);
          setImageFileSizeBytes(bytes);
          if (bytes > 1024 * 1024) setImageFileSize(`${(bytes / 1024 / 1024).toFixed(1)} MB`);
          else setImageFileSize(`${(bytes / 1024).toFixed(0)} KB`);
        }
      })
      .catch(() => {
        fetch(fullImageUrl)
          .then(res => res.blob())
          .then(blob => {
            setImageFileSizeBytes(blob.size);
            if (blob.size > 1024 * 1024) setImageFileSize(`${(blob.size / 1024 / 1024).toFixed(1)} MB`);
            else setImageFileSize(`${(blob.size / 1024).toFixed(0)} KB`);
          })
          .catch(() => { });
      });
  }, [fullImageUrl]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const clampedDelta = Math.max(-30, Math.min(30, e.deltaY));
    const factor = 1 - clampedDelta * 0.001;
    targetScaleRef.current = Math.min(Math.max(targetScaleRef.current * factor, 0.1), 10);

    if (scaleAnimRef.current) return;

    const animate = () => {
      setScale(prev => {
        const diff = targetScaleRef.current - prev;
        if (Math.abs(diff) < 0.001) {
          scaleAnimRef.current = null;
          return targetScaleRef.current;
        }
        scaleAnimRef.current = requestAnimationFrame(animate);
        return prev + diff * 0.25;
      });
    };
    scaleAnimRef.current = requestAnimationFrame(animate);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const resetView = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    targetScaleRef.current = 1;
    if (scaleAnimRef.current) {
      cancelAnimationFrame(scaleAnimRef.current);
      scaleAnimRef.current = null;
    }
  }, []);

  const prevIndexRef = useRef(currentIndex);
  useEffect(() => {
    if (!isGroupMode || galleryImages.length === 0 || !galleryImages[currentIndex]) return;
    const isIndexChange = prevIndexRef.current !== currentIndex;
    prevIndexRef.current = currentIndex;

    if (isIndexChange) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      targetScaleRef.current = 1;
      setImageDimensions(null);
      setImageFileSize(null);
      setImageFileSizeBytes(0);
      setPublished(false);
    }
  }, [currentIndex, isGroupMode, galleryImages]);

  const goPrev = useCallback(() => setCurrentIndex(i => Math.max(0, i - 1)), []);
  const goNext = useCallback(() => setCurrentIndex(i => Math.min(galleryImages.length - 1, i + 1)), [galleryImages.length]);

  const showActionMsg = (msg: string, type: "success" | "error" = "success") => {
    setActionMsg({ text: msg, type });
    setTimeout(() => setActionMsg(null), 3000);
  };

  const handleDownload = async () => {
    try {
      await downloadFile(fullImageUrl, `flowx-${task.uuid.slice(0, 8)}.png`);
      showActionMsg("下载成功", "success");
    } catch (err) {
      if (isDownloadCancelError(err)) return;
      showActionMsg("下载失败，请重试", "error");
    }
  };

  const handleSaveToInspiration = async () => {
    if (!fullImageUrl) {
      showActionMsg("图片加载中，请稍后再试", "error");
      return;
    }
    try {
      const result = await toggleFavorite();
      if (typeof result === "boolean") showActionMsg(result ? "已收藏到我的收藏" : "已取消收藏", "success");
    } catch {
      showActionMsg("收藏操作失败，请重试", "error");
    }
  };

  const handlePublishToInspiration = () => {
    if (publishing || published) return;
    if (!fullImageUrl) { showActionMsg("图片加载中，请稍后再试", "error"); return; }
    setPublishing(true);
  };

  const featureKeyToName = (key: string): string => {
    const match = workflowOptions.find(o => o.feature_key === key);
    return match?.name || key || "—";
  };

  return (
    <>
    <InspirationUploadModal open={publishing} onClose={() => setPublishing(false)} onAfterClose={() => {}}
      onSuccess={() => { setPublished(true); showActionMsg("已发布到灵感社区", "success"); }}
      initialImageUrl={fullImageUrl} initialPrompt={promptText} initialTitle={task.title || "AI生成图片"} layer="nestedModal" />
    <div
      className="flowx-ecom-image fixed inset-0 z-300 flex bg-black/85 backdrop-blur-md"
      onClick={onClose}
    >
      {/* Action toast */}
      {actionMsg && (
        <div className={`absolute top-5 left-1/2 -translate-x-1/2 z-40 px-5 py-2.5
                        text-white text-sm rounded-lg shadow-lg
                        animate-[fadeInDown_0.3s_ease-out] ${actionMsg.type === "error"
            ? "bg-red-500/90"
            : "bg-emerald-500/90"
          }`}>
          {actionMsg.text}
        </div>
      )}

      {/* Image area */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden select-none outline-hidden relative"
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
        onClick={e => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <SafeImage
          src={fullImageUrl}
          alt="预览"
          draggable={false}
          decoding="async"
          className="max-w-none outline-hidden border-0"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            maxHeight: "88vh",
            maxWidth: "70vw",
            objectFit: "contain",
          }}
        />

        {/* Gallery navigation arrows */}
        {isGroupMode && galleryImages.length > 1 && (
          <>
            {currentIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                onMouseDown={e => e.stopPropagation()}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10
                           rounded-full bg-black/60 hover:bg-black/80 border border-white/15
                           flex items-center justify-center text-white/80 hover:text-white
                           transition-all hover:scale-110"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            {currentIndex < galleryImages.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                onMouseDown={e => e.stopPropagation()}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10
                           rounded-full bg-black/60 hover:bg-black/80 border border-white/15
                           flex items-center justify-center text-white/80 hover:text-white
                           transition-all hover:scale-110"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20
                            bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 border border-white/10
                            text-xs text-white/70 tabular-nums">
              {currentIndex + 1} / {galleryImages.length}
            </div>
          </>
        )}
      </div>

      {/* Zoom toolbar */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1
                    bg-black/70 backdrop-blur-lg rounded-full px-2 py-1.5 border border-white/10"
        onClick={e => e.stopPropagation()}
      >
        <button onClick={() => setScale(s => Math.max(s - 0.25, 0.2))}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14" strokeLinecap="round" /></svg>
        </button>
        <span className="text-xs text-white/60 w-12 text-center tabular-nums">{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.min(s + 0.25, 5))}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>
        </button>
        <div className="w-px h-5 bg-white/15 mx-0.5" />
        <button onClick={resetView}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          title="重置视图">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 3v5h5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Right info panel */}
      <div
        className="w-75 shrink-0 bg-[#141414]/95 border-l border-white/8
                    flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Panel header with back button */}
        <div className="px-4 pt-4 pb-3 border-b border-white/6">
          <button
            onClick={onClose}
            className="w-full h-9 mb-3 flex items-center justify-center gap-2 rounded-lg
                       bg-white/8 hover:bg-white/15 border border-white/12
                       text-white/80 hover:text-white text-sm transition-all active:scale-[0.98]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            返回生图页面
          </button>
          <h3 className="text-sm font-semibold text-white mb-1">图片详情</h3>
          <div className="text-xs text-white/40">
            {featureKeyToName(task.feature_key || task.task_type)}
          </div>
        </div>

        {/* Metadata */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-thumb]:rounded-full">
          {/* Dimensions */}
          <div>
            <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">尺寸</div>
            <div className="text-sm text-white/80">
              {imageDimensions ? `${imageDimensions.w} × ${imageDimensions.h} px` : "加载中..."}
            </div>
          </div>

          {/* File size */}
          {imageFileSize && (
            <div>
              <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">文件大小</div>
              <div className="text-sm text-white/80">{imageFileSize}</div>
            </div>
          )}

          {/* Created time */}
          <div>
            <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">生成时间</div>
            <div className="text-sm text-white/80">{formatFullTime(task.created_at)}</div>
          </div>

          {/* Completed time */}
          {(isGroupMode ? galleryCompletedTimes[currentIndex] : task.completed_at) && (
            <div>
              <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">完成时间</div>
              <div className="text-sm text-white/80">
                {formatFullTime(isGroupMode ? galleryCompletedTimes[currentIndex] : task.completed_at)}
              </div>
            </div>
          )}

          {/* Image count (group mode) */}
          {isGroupMode && groupTaskCount > 0 && (
            <div>
              <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">图片数量</div>
              <div className="text-sm text-white/80">{galleryImages.length} 张</div>
            </div>
          )}

          {/* Points cost */}
          {(isGroupMode ? groupPointsCost : task.points_cost) > 0 && (
            <div>
              <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">消耗</div>
              <div className="text-sm font-medium">
                <DouziAmount amount={isGroupMode ? groupPointsCost : task.points_cost} />
              </div>
            </div>
          )}

          {/* Prompt */}
          {promptText && (
            <div>
              <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">提示词</div>
              <div className="text-sm text-white/70 leading-relaxed bg-white/3 rounded-lg p-3 border border-white/6 break-all">
                {promptText}
              </div>
            </div>
          )}

          {/* Task ID */}
          <div>
            <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">
              {isGroupMode ? "任务组ID" : "任务ID"}
            </div>
            <div className="text-xs text-white/40 font-mono break-all">
              {isGroupMode ? groupId : task.uuid}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-5 py-4 border-t border-white/6 space-y-2.5">
          <EcomButton
            variant="default"
            size="large"
            onClick={handleDownload}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            下载图片
          </EcomButton>
          <div className="grid grid-cols-2 gap-2">
            <EcomButton
              variant="outline"
              size="small"
              onClick={handleSaveToInspiration}
              disabled={saving || checkingFavorite || !favoriteTaskUUID}
              loading={saving}
              aria-pressed={isFavorited}
            >
              {!saving && <Heart size={14} className="text-white" fill={isFavorited ? "currentColor" : "none"} />}
              {saving ? "收藏中..." : checkingFavorite ? "查询收藏中..." : favoriteLookupFailed ? "重试收藏状态" : isFavorited ? "已收藏" : "收藏"}
            </EcomButton>
            <EcomButton
              variant="outline"
              size="small"
              onClick={handlePublishToInspiration}
              disabled={publishing || published}
              loading={publishing}
            >
              {!publishing && (
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {published
                    ? <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    : <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" />
                  }
                </svg>
              )}
              {publishing ? "发布中..." : published ? "已发布" : "发布灵感"}
            </EcomButton>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

const HISTORY_PAGE_SIZE = 30;

export default function HistoryPanel({
  variant = "panel",
  className = "",
  onTaskCompleted,
  refreshTrigger,
  teamId,
  workflowOptions = [],
  currentTaskIds = [],
  currentProgress = 0,
  taskProgresses = [],
  currentGeneratedImages = [],
  currentFailedIndexes = [],
  currentStatus,
  currentInspireResults = [],
  onActiveTaskClick,
  onRegenerateFromHistory,
  onOpenAgentHistory,
  agentController,
  onOpenAgentConversation,
  agentFeatureKeys = [],
  restoringAgentTaskId,
  onExpand,
  onCollapse,
  regeneratingTaskUuids = [],
  refiningTaskUuids = [],
  includeFailed = false,
  filterKey,
  onFilterChange,
  onViewImageLayer,
}: HistoryPanelProps) {
  const feedback = useEcomFeedback();
  const { run: runEcomPollingRequest } = useEcomPollingFeedback();
  const getRegularHistoryTasks = useCallback(
    (params: Parameters<typeof ecomImageApi.getRegularHistoryTasks>[0]) => runEcomPollingRequest(
      `ecom-image:regular-history:${JSON.stringify(params)}`,
      () => ecomImageApi.getRegularHistoryTasks(params),
    ),
    [runEcomPollingRequest],
  );
  const getBatchTaskStatus = useCallback(
    (parentId: string) => runEcomPollingRequest(
      `ecom-image:batch-task-status:${parentId}`,
      () => ecomImageApi.getBatchTaskStatus(parentId),
    ),
    [runEcomPollingRequest],
  );
  const [history, setHistory] = useState<GenerateTask[]>([]);
  const [agentHistory, setAgentHistory] = useState<AgentConversation[]>([]);
  const [agentCursors, setAgentCursors] = useState<AgentHistoryCursors>({});
  const [agentHasMore, setAgentHasMore] = useState(false);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState(false);
  const [agentRetryKey, setAgentRetryKey] = useState(0);
  const [openingConversationId, setOpeningConversationId] = useState<string | null>(null);
  const agentRequestRef = useRef(0);
  const regularCursorRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [internalFilterKey, setInternalFilterKey] = useState<string>(filterKey ?? "all");
  const activeFilterKey = filterKey ?? internalFilterKey;
  const setActiveFilterKey = onFilterChange ?? setInternalFilterKey;
  const [hasMore, setHasMore] = useState(true);
  // Newest creation day of this user's tasks archived to OSS; set on the last history page.
  const [archivedThrough, setArchivedThrough] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [previewTask, setPreviewTask] = useState<GenerateTask | null>(null);
  const [previewGroupId, setPreviewGroupId] = useState<string | null>(null);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [batchChildrenMap, setBatchChildrenMap] = useState<Record<string, BatchTaskChild[]>>({});
  const [portraitThumbs, setPortraitThumbs] = useState<Set<string>>(new Set());
  const [historyImageMeta, setHistoryImageMeta] = useState<Record<string, { dimensions?: string; fileSize?: string }>>({});
  const [detailGroup, setDetailGroup] = useState<HistoryGroupDescriptor | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const detailGroupClosingRef = useRef<HistoryGroupDescriptor | null>(null);
  const [imageLayerHistoryTasks, setImageLayerHistoryTasks] = useState<Record<string, GenerateTask>>({});
  const [imageLayerTaskDetails, setImageLayerTaskDetails] = useState<Record<string, ImageLayerTask>>({});
  const [openingImageLayerId, setOpeningImageLayerId] = useState<string | null>(null);
  /** slotId+url -> load failed; declarative fallback (avoids DOM appendChild breaking React reconciliation). */
  const [failedThumbs, setFailedThumbs] = useState<Record<string, boolean>>({});
  const completedNotifiedRef = useRef<Set<string>>(new Set());
  const initialMarkDoneRef = useRef(false);
  const historyImageHeadRequestedRef = useRef<Set<string>>(new Set());
  const listScrollRef = useRef<HTMLDivElement>(null);
  /**
   * 点击"查看更多"时记录追加前的 scrollHeight；
   * 追加成功后由 useEffect 通过 rAF 把视口滚到新追加内容的起点，
   * 避免用户停留在已经看过的上方记录。
   */
  const pendingLoadMoreAnchorRef = useRef<number | null>(null);
  const prevHistoryLengthRef = useRef(0);
  const prevTeamIdRef = useRef(teamId);
  /**
   * 持有最新的 currentPage 供 refreshLoadedHistory 等"刷新型"回调读取。
   * 直接把 currentPage 放进 useCallback 的依赖数组，会让轮询/刷新副作用在
   * 每次点击查看更多时重建，从而打断已建立的 setInterval。
   */
  const currentPageRef = useRef(1);
  const effectiveWorkflowFilter = (activeFilterKey !== "all" ? activeFilterKey : "").trim();
  const historyScopeKey = `${teamId ?? ""}:${activeFilterKey}:${includeFailed}`;
  const historyScopeRef = useRef(historyScopeKey);
  historyScopeRef.current = historyScopeKey;
  const showsAgentHistory = !!agentController && (activeFilterKey === "all" || agentFeatureKeys.includes(activeFilterKey) || activeFilterKey === "agent");
  // Joined so the inline-built prop array does not recreate the history loaders on every render.
  const agentModuleKeysSignature = agentHistoryModuleKeys(activeFilterKey, agentFeatureKeys).join("\n");
  const agentModuleKeys = useMemo(() => agentModuleKeysSignature.split("\n"), [agentModuleKeysSignature]);
  const readAgentHistoryPage = useCallback<AgentHistoryPageReader>(
    (cursor, limit, moduleKey) => agentController
      ? agentController.listHistoryPage(cursor, limit, moduleKey)
      : Promise.resolve({ items: [], nextCursor: null }),
    [agentController],
  );
  const showsRegularHistory = activeFilterKey === "all" || !showsAgentHistory;

  const openAgentConversation = useCallback(async (conversationId: string) => {
    if (!onOpenAgentConversation || openingConversationId) return;
    setOpeningConversationId(conversationId);
    try { await onOpenAgentConversation(conversationId); }
    finally { setOpeningConversationId(null); }
  }, [onOpenAgentConversation, openingConversationId]);

  const closeHistoryRecordModal = useCallback(() => {
    detailGroupClosingRef.current = detailGroup;
    setDetailModalOpen(false);
  }, [detailGroup]);

  const handleViewImageLayer = useCallback(async (group: HistoryGroup) => {
    if (!onViewImageLayer) return;
    const sourceTask = group.tasks.find((item) => item.task_type === "seedream_layer" || item.feature_key === "seedream_layer") || group.tasks[0];
    if (!sourceTask?.uuid || openingImageLayerId === sourceTask.uuid) return;
    setOpeningImageLayerId(sourceTask.uuid);
    try {
      const detail = imageLayerTaskDetails[sourceTask.uuid] || await getImageLayerTask(sourceTask.uuid, {
        taskKind: sourceTask.task_type === "seedream_layer" ? "seedream_layer" : undefined,
        fallbackImageUrl: sourceTask.input_images?.[0] || sourceTask.thumbnail || "",
      });
      setImageLayerTaskDetails((prev) => ({ ...prev, [sourceTask.uuid]: detail }));
      onViewImageLayer(detail, detail.source_image_url || detail.image?.url || sourceTask.input_images?.[0] || sourceTask.thumbnail || "");
    } catch {
      feedback.error({ code: "E5022", fallback: "operation" });
    } finally {
      setOpeningImageLayerId(null);
    }
  }, [feedback, imageLayerTaskDetails, onViewImageLayer, openingImageLayerId]);

  const finishHistoryRecordModalClose = useCallback(() => {
    const closingGroup = detailGroupClosingRef.current;
    detailGroupClosingRef.current = null;
    setDetailGroup((current) => (
      current === closingGroup ? null : current
    ));
  }, []);

  const handleThumbLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>, sourceUrl?: string) => {
    const img = e.currentTarget;
    if (img.naturalHeight > img.naturalWidth * 1.3) {
      setPortraitThumbs(prev => {
        if (prev.has(img.src)) return prev;
        const next = new Set(prev);
        next.add(img.src);
        return next;
      });
    }
    const metaKey = sourceUrl?.trim();
    if (!metaKey) return;
    const dimensions = img.naturalWidth > 0 && img.naturalHeight > 0
      ? `${img.naturalWidth} x ${img.naturalHeight}`
      : "";
    if (dimensions) {
      setHistoryImageMeta(prev => {
        if (prev[metaKey]?.dimensions === dimensions) return prev;
        return { ...prev, [metaKey]: { ...prev[metaKey], dimensions } };
      });
    }
    if (!historyImageHeadRequestedRef.current.has(metaKey)) {
      historyImageHeadRequestedRef.current.add(metaKey);
      void fetchHistoryImageContentLength(metaKey)
        .then((length) => {
          const fileSize = formatBytes(length ?? 0);
          if (!fileSize) return;
          setHistoryImageMeta(prev => {
            if (prev[metaKey]?.fileSize === fileSize) return prev;
            return { ...prev, [metaKey]: { ...prev[metaKey], fileSize } };
          });
        })
        .catch(() => {
          // Metadata is optional; the history list should never wait on it.
        });
    }
  }, []);

  const markThumbFailed = useCallback((slotId: string, url: string) => {
    if (!url) return;
    setFailedThumbs((p) => ({ ...p, [thumbFailMapKey(slotId, url)]: true }));
  }, []);

  const clearThumbFailed = useCallback((slotId: string, url: string) => {
    if (!url) return;
    const k = thumbFailMapKey(slotId, url);
    setFailedThumbs((p) => {
      if (!p[k]) return p;
      const n = { ...p };
      delete n[k];
      return n;
    });
  }, []);

  // Reset state when team changes
  useEffect(() => {
    if (prevTeamIdRef.current === teamId) return;
    prevTeamIdRef.current = teamId;
    setHistory([]);
    setActiveFilterKey("all");
    completedNotifiedRef.current = new Set();
    initialMarkDoneRef.current = false;
  }, [teamId, setActiveFilterKey]);

  useEffect(() => {
    setImageLayerHistoryTasks({});
    setImageLayerTaskDetails({});
  }, [teamId]);

  const fetchHistory = useCallback(async (_page = 1, append = false) => {
    if (!showsRegularHistory) {
      regularCursorRef.current = null;
      setHistory([]);
      setHasMore(false);
      setArchivedThrough("");
      return [];
    }
    try {
      const cursor = append ? regularCursorRef.current : null;
      const params = { page_size: HISTORY_PAGE_SIZE,
        ...(cursor ? { cursor } : {}),
        ...(effectiveWorkflowFilter ? { feature_key: effectiveWorkflowFilter } : {}) };
      const res = await getRegularHistoryTasks(params);
      if (historyScopeRef.current !== historyScopeKey) return null;
      if (res.data?.items) {
        const items = (includeFailed
          ? res.data.items
          : res.data.items.filter(t => t.status !== TASK_STATUS.FAILED)).map(projectAgentHistoryTask);
        regularCursorRef.current = res.data.next_cursor;
        if (append) {
          setHistory(prev => [...prev, ...items.filter(item => !prev.some(old => old.uuid === item.uuid))]);
        } else {
          setHistory(items);
        }
        setHasMore(!!res.data.next_cursor);
        setArchivedThrough(res.data.next_cursor ? "" : res.data.archived_through ?? "");
        return items;
      }
    } catch (err) {
      console.error("Failed to fetch history:", err);
    }
    return null;
  }, [effectiveWorkflowFilter, getRegularHistoryTasks, historyScopeKey, includeFailed, showsRegularHistory]);

  /**
   * 刷新已加载的历史范围而不回退到第 1 页。
   *
   * 用户通过"查看更多"加载到第 N 页时，定时轮询 / refreshTrigger / 删除任务
   * 等场景都需要保持下方已加载内容不被覆盖。这里按当前页数一次性拉取
   * page_size = HISTORY_PAGE_SIZE * currentPage 条，重写 history 后保留
   * 排序与状态一致，避免出现"下方内容突然隐藏"的体验问题。
   */
  const refreshLoadedHistory = useCallback(async () => {
    if (!showsRegularHistory) return [];
    try {
      const items: GenerateTask[] = [];
      let cursor: string | null = null;
      for (let index = 0; index < currentPageRef.current; index++) {
        const res = await getRegularHistoryTasks({ page_size: HISTORY_PAGE_SIZE,
          ...(cursor ? { cursor } : {}),
          ...(effectiveWorkflowFilter ? { feature_key: effectiveWorkflowFilter } : {}) });
        if (historyScopeRef.current !== historyScopeKey) return null;
        if (!res.data?.items) return null;
        items.push(...res.data.items);
        cursor = res.data.next_cursor;
        if (!cursor) {
          setArchivedThrough(res.data.archived_through ?? "");
          break;
        }
      }
      regularCursorRef.current = cursor;
      const visible = (includeFailed ? items : items.filter(t => t.status !== TASK_STATUS.FAILED)).map(projectAgentHistoryTask);
      setHistory(visible);
      setHasMore(!!cursor);
      return visible;
    } catch (err) {
      console.error("Failed to refresh history:", err);
    }
    return null;
  }, [effectiveWorkflowFilter, getRegularHistoryTasks, historyScopeKey, includeFailed, showsRegularHistory]);

  useEffect(() => {
    const handleImageLayerHistorySync = (event: Event) => {
      const detail = (event as CustomEvent<{ task?: ImageLayerTask; imageUrl?: string }>).detail;
      if (!detail?.task?.task_uuid) return;
      const syncedTask = detail.task;
      const historyTask = imageLayerTaskToHistoryTask(syncedTask, detail.imageUrl);
      setImageLayerHistoryTasks((prev) => ({
        ...prev,
        [historyTask.uuid]: historyTask,
      }));
      setImageLayerTaskDetails((prev) => ({
        ...prev,
        [syncedTask.task_uuid]: syncedTask,
      }));
      if (!isActiveTaskStatus(historyTask.status)) {
        void refreshLoadedHistory();
      }
    };

    window.addEventListener(IMAGE_LAYER_HISTORY_SYNC_EVENT, handleImageLayerHistorySync);
    return () => window.removeEventListener(IMAGE_LAYER_HISTORY_SYNC_EVENT, handleImageLayerHistorySync);
  }, [refreshLoadedHistory]);

  useEffect(() => {
    if (history.length === 0) return;
    const fetchedById = new Map(history.map((item) => [item.uuid, item]));
    setImageLayerHistoryTasks((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((uuid) => {
        const fetched = fetchedById.get(uuid);
        if (fetched && !isActiveTaskStatus(fetched.status)) {
          delete next[uuid];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [history]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || (!hasMore && !agentHasMore)) return;
    const scopeKey = historyScopeKey;
    const agentRequestId = agentRequestRef.current;
    setLoadingMore(true);
    const container = listScrollRef.current;
    if (container) {
      pendingLoadMoreAnchorRef.current = container.scrollHeight;
    }
    const nextPage = nextHistoryPage(currentPage);
    const requests: Promise<unknown>[] = [];
    if (hasMore) requests.push(fetchHistory(nextPage, true));
    if (agentHasMore && agentController) {
      requests.push(loadNextAgentHistoryPage(readAgentHistoryPage, agentCursors, 30).then((page) => {
        if (historyScopeRef.current !== scopeKey || agentRequestRef.current !== agentRequestId) return;
        setAgentHistory(prev => [...prev, ...page.items.filter(item => !prev.some(old => old.conversationId === item.conversationId))]);
        setAgentCursors(page.cursors);
        setAgentHasMore(hasMoreAgentHistory(page.cursors));
      }).catch(() => {
        if (historyScopeRef.current === scopeKey && agentRequestRef.current === agentRequestId) setAgentError(true);
      }));
    }
    await Promise.all(requests);
    if (historyScopeRef.current === scopeKey) setCurrentPage(nextPage);
    setLoadingMore(false);
  }, [loadingMore, hasMore, agentHasMore, agentController, agentCursors, readAgentHistoryPage, currentPage, fetchHistory, historyScopeKey]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const refreshAgentHistory = useCallback(async (showLoading: boolean) => {
    const requestId = ++agentRequestRef.current;
    if (!showsAgentHistory || !agentController) {
      setAgentHistory([]);
      setAgentCursors({});
      setAgentHasMore(false);
      setAgentError(false);
      setAgentLoading(false);
      return;
    }
    if (showLoading) setAgentLoading(true);
    try {
      const { items, cursors } = await loadAgentHistoryPages(readAgentHistoryPage, agentModuleKeys, currentPageRef.current, 30);
      if (agentRequestRef.current !== requestId) return;
      setAgentHistory(items);
      setAgentCursors(cursors);
      setAgentHasMore(hasMoreAgentHistory(cursors));
      setAgentError(false);
    } catch {
      if (agentRequestRef.current === requestId) setAgentError(true);
    } finally {
      if (agentRequestRef.current === requestId) setAgentLoading(false);
    }
  }, [agentController, showsAgentHistory, agentModuleKeys, readAgentHistoryPage]);

  useEffect(() => {
    void refreshAgentHistory(true);
    return () => { agentRequestRef.current++; };
  }, [refreshAgentHistory, activeFilterKey, teamId, refreshTrigger, agentRetryKey]);

  useEffect(() => {
    if (!showsAgentHistory) return;
    // A conversation can enter or leave a Run while this panel is open. Keep
    // its persisted summary current, including sessions with no image yet.
    const pollController = startEcomPollingLoop({
      poll: () => refreshAgentHistory(false),
      baseDelayMs: 5000,
      startImmediately: false,
    });
    return () => pollController.stop();
  }, [refreshAgentHistory, showsAgentHistory]);

  // 查看更多追加后，把视口滚到新追加内容的起点，避免用户还要手动往下找。
  useEffect(() => {
    const anchor = pendingLoadMoreAnchorRef.current;
    const prevLength = prevHistoryLengthRef.current;
    prevHistoryLengthRef.current = history.length;
    if (anchor === null) return;
    if (history.length <= prevLength) return;
    pendingLoadMoreAnchorRef.current = null;
    const container = listScrollRef.current;
    if (!container) return;
    const id = requestAnimationFrame(() => {
      // 留出 80px 的重叠，让"老内容 → 新内容"过渡线对用户可见，
      // 同时保证新追加的记录第一条直接出现在可视区域。
      const target = Math.max(0, anchor - 80);
      container.scrollTo({ top: target, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [history.length]);

  const fetchBatchChildren = useCallback(async (parentTaskId: string) => {
    try {
      const res = await getBatchTaskStatus(parentTaskId);
      if (res.data?.children) {
        setBatchChildrenMap(prev => ({ ...prev, [parentTaskId]: res.data!.children }));
      }
    } catch (err) {
      console.error("Failed to fetch batch children:", err);
    }
  }, []);

  // Reload on trigger, filter change, or team change (with retries for stale backend data)
  const prevRefreshTrigger = useRef(refreshTrigger);
  useEffect(() => {
    let cancelled = false;
    const isRefreshOnly = prevRefreshTrigger.current !== refreshTrigger && history.length > 0;
    prevRefreshTrigger.current = refreshTrigger;
    // 仅在筛选 / 团队切换等"完全重载"场景把列表重置回第 1 页；
    // 单纯的 refreshTrigger 触发（生成新任务等）保留用户通过"查看更多"
    // 加载的页数，避免下方记录被覆盖隐藏。
    if (!isRefreshOnly) {
      setCurrentPage(1);
      setHasMore(true);
    }
    const load = async (attempt = 0) => {
      if (!isRefreshOnly) setLoading(true);
      const items = isRefreshOnly
        ? await refreshLoadedHistory()
        : await fetchHistory(1, false);
      if (cancelled) return;
      // A transient gateway restart or per-token 429 must not leave the
      // restored history panel permanently blank. The HTTP layer performs
      // immediate retries; this effect retries the whole page load twice more
      // after its backoff so the existing component state can self-heal.
      if (!items && attempt < 2) {
        await new Promise(resolve => setTimeout(resolve, 1500 * 2 ** attempt));
        if (!cancelled) await load(attempt + 1);
        return;
      }
      setLoading(false);
      if (!items) return;
      const stillActive = items.some((t) => isActiveTaskStatus(t.status));
      if (stillActive && (refreshTrigger ?? 0) > 0) {
        const retries = [2000, 4000];
        for (const delay of retries) {
          if (cancelled) break;
          await new Promise(r => setTimeout(r, delay));
          if (cancelled) break;
          const fresh = isRefreshOnly
            ? await refreshLoadedHistory()
            : await fetchHistory(1, false);
          if (!fresh) break;
          const active = fresh.some((t) => isActiveTaskStatus(t.status));
          if (!active) break;
        }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, fetchHistory, refreshLoadedHistory, teamId]);

  // 记录本页以生成中/失败态出现过的任务；完成后历史接口未刷新前只为这些任务保留合成卡片，
  // 从历史恢复参数的已完成任务不会进入这里。
  const submittedTaskIdsRef = useRef<Set<string>>(new Set());
  if (shouldCreateSyntheticCurrentTask(currentStatus)) {
    for (const taskId of currentTaskIds) {
      if (taskId) submittedTaskIdsRef.current.add(taskId);
    }
  }

  // 当前页正在生成的任务尚未出现在历史列表时，立即拉取一次
  const prevCurrentTaskIdsRef = useRef<string[]>([]);
  useEffect(() => {
    if (currentTaskIds.length === 0) {
      prevCurrentTaskIdsRef.current = [];
      return;
    }
    const prev = prevCurrentTaskIdsRef.current;
    const changed =
      currentTaskIds.length !== prev.length ||
      currentTaskIds.some((id, i) => id !== prev[i]);
    if (!changed) return;
    prevCurrentTaskIdsRef.current = currentTaskIds;
    void refreshLoadedHistory();
  }, [currentTaskIds, refreshLoadedHistory]);

  // Auto-poll for active tasks (含：进行中任务尚未写入历史列表的情况)
  useEffect(() => {
    const hasActiveSyncedImageLayerTask = Object.values(imageLayerHistoryTasks).some(
      (t) => isActiveTaskStatus(t.status),
    );
    const hasUntrackedActiveHistory = history.some(
      (t) => isActiveTaskStatus(t.status) && !currentTaskIds.includes(t.uuid),
    );
    if (!hasUntrackedActiveHistory && !hasActiveSyncedImageLayerTask) return;

    const pollController = startEcomPollingLoop({
      poll: async () => {
        const items = await refreshLoadedHistory();
        if (!items || !initialMarkDoneRef.current) return;

        for (const item of items) {
          if (
            item.status === TASK_STATUS.COMPLETED &&
            !completedNotifiedRef.current.has(item.uuid)
          ) {
            completedNotifiedRef.current.add(item.uuid);
            // 跳过主轮询正在跟踪的任务，由主页面轮询负责更新 UI
            if (currentTaskIds.includes(item.uuid)) continue;
            try {
              const detail = await ecomImageApi.getTask(item.uuid);
              const outputImages = resolveTaskOutputImages(
                detail.data?.output_images,
                detail.data?.coze_raw_output,
              );
              const modelImg = detail.data?.model_image || '';
              const productImg = detail.data?.product_image || '';
              if (outputImages && outputImages.length > 0) {
                onTaskCompleted?.({
                  effectImage: outputImages[0],
                  modelImage: modelImg,
                  productImage: productImg,
                  taskId: item.uuid,
                  featureKey: item.feature_key || item.task_type,
                });
              } else if (item.thumbnail) {
                onTaskCompleted?.({
                  effectImage: item.thumbnail,
                  modelImage: modelImg,
                  productImage: productImg,
                  taskId: item.uuid,
                  featureKey: item.feature_key || item.task_type,
                });
              }
            } catch {
              if (item.thumbnail) {
                onTaskCompleted?.({
                  effectImage: item.thumbnail,
                  modelImage: '',
                  productImage: '',
                  taskId: item.uuid,
                  featureKey: item.feature_key || item.task_type,
                });
              }
            }
            break;
          }
        }
      },
      baseDelayMs: 3000,
      startImmediately: false,
    });

    return () => pollController.stop();
  }, [history, refreshLoadedHistory, onTaskCompleted, currentTaskIds, imageLayerHistoryTasks]);

  // Mark already-completed tasks on initial load
  useEffect(() => {
    if (!initialMarkDoneRef.current && history.length > 0) {
      initialMarkDoneRef.current = true;
      history.forEach((item) => {
        if (item.status === TASK_STATUS.COMPLETED) {
          completedNotifiedRef.current.add(item.uuid);
        }
      });
    }
  }, [history]);

  // Auto-expand the group that contains the currently generating tasks
  useEffect(() => {
    if (currentTaskIds.length <= 1) return;
    for (const group of history) {
      if (group.group_id && currentTaskIds.includes(group.uuid)) {
        setExpandedGroupId(group.group_id);
        return;
      }
    }
  }, [currentTaskIds, history]);

  // Auto-expand active batch parent tasks so subtask progress is visible immediately
  useEffect(() => {
    if (expandedGroupId) return; // don't override manual selection
    for (const item of history) {
      if (item.is_batch_parent && isActiveTaskStatus(item.status)) {
        const key = item.group_id || item.uuid;
        setExpandedGroupId(key);
        fetchBatchChildren(item.uuid);
        return;
      }
    }
  }, [history, expandedGroupId, fetchBatchChildren]);

  // Auto-poll batch children for active batch parents AND parents with regenerating/refining children
  useEffect(() => {
    const regenSet = new Set(regeneratingTaskUuids);
    const refineSet = new Set(refiningTaskUuids);
    const activeBatchParents = history.filter(t => {
      if (!t.is_batch_parent) return false;
      if (isActiveTaskStatus(t.status)) return true;
      const children = batchChildrenMap[t.uuid];
      if (children) {
        if (regenSet.size > 0 && children.some(c => regenSet.has(c.uuid))) return true;
        if (refineSet.size > 0 && children.some(c => refineSet.has(c.uuid))) return true;
      }
      return false;
    });
    if (activeBatchParents.length === 0) return;

    const pollController = startEcomPollingLoop({
      poll: async () => {
        await Promise.all(activeBatchParents.map((parent) => fetchBatchChildren(parent.uuid)));
      },
      baseDelayMs: 3000,
    });
    return () => pollController.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, fetchBatchChildren, regeneratingTaskUuids, refiningTaskUuids]);

  const displayHistory = (() => {
    const syncedImageLayerTasks = Object.values(imageLayerHistoryTasks)
      .sort((a, b) => getHistoryTimestamp(b.created_at) - getHistoryTimestamp(a.created_at));
    const syncedIds = new Set(syncedImageLayerTasks.map((item) => item.uuid));
    const baseHistory = syncedImageLayerTasks.length > 0
      ? [...syncedImageLayerTasks, ...history.filter((item) => !syncedIds.has(item.uuid))]
      : history;
    const dedupedBaseHistory = dedupeTextSeparationHistory(baseHistory, syncedIds, imageLayerTaskDetails);

    // 工作流筛选只在前端拉取时作为 feature_key 传给后端，但合成任务
    // (syntheticTasks)、图层同步任务 (syncedImageLayerTasks) 以及 overlay
    // 都是在客户端拼装、会绕过服务端筛选。这里统一在最终结果上再过滤一遍，
    // 避免选了某个工作流后，顶上仍钉着一条不匹配的「反推」/当前任务。
    const filterByWorkflow = (items: GenerateTask[]) => effectiveWorkflowFilter
      ? items.filter((item) => (item.feature_key || "") === effectiveWorkflowFilter)
      : items;

    if (currentTaskIds.length === 0) return filterByWorkflow(dedupedBaseHistory);

    const now = new Date().toISOString();
    const failedIndexSet = new Set(currentFailedIndexes);
    const allCurrentTasksFailed = currentStatus === "failed" && currentTaskIds.length > 0;
    const currentInspireByTaskId = new Map(
      currentInspireResults
        .filter((item) => item.taskId && !item.taskId.startsWith("pending-"))
        .map((item) => [item.taskId, item]),
    );
    const overlaid = dedupedBaseHistory.map((item) => {
      const currentIndex = currentTaskIds.indexOf(item.uuid);
      if (currentIndex < 0) return item;

      const generatedImage = (currentGeneratedImages[currentIndex] || "").trim();
      const inspireMeta = currentInspireByTaskId.get(item.uuid);
      const inspireStatus = mapInspireStatusToTaskStatus(inspireMeta?.status);
      const isFailed = failedIndexSet.has(currentIndex) || allCurrentTasksFailed || inspireStatus === TASK_STATUS.FAILED;
      const nextProgress = Math.max(
        item.progress || 0,
        taskProgresses[currentIndex] || 0,
        currentProgress || 0,
        generatedImage || isFailed ? 100 : 0,
      );
      const inputImages = inspireMeta?.inputImages?.filter(Boolean) || item.input_images || [];
      const inspireThumb = generatedImage || item.thumbnail || inputImages[0] || "";

      return {
        ...item,
        status: generatedImage
          ? TASK_STATUS.COMPLETED
          : isFailed
            ? TASK_STATUS.FAILED
            : inspireMeta
              ? inspireStatus
              : resolveHistoryDisplayStatus({
                originalStatus: item.status,
                currentStatus,
                hasGeneratedImage: !!generatedImage,
                isFailed,
              }),
        progress: nextProgress,
        thumbnail: inspireThumb,
        input_images: inputImages,
        prompt: inspireMeta?.prompt || item.prompt,
        inspire_prompt: inspireMeta?.prompt || item.inspire_prompt,
        completed_at: generatedImage ? (item.completed_at || now) : item.completed_at,
      };
    });

    const missingCurrentTasks = currentTaskIds
      .map((taskId, index) => {
        const generatedImage = (currentGeneratedImages[index] || "").trim();
        return { taskId, index, generatedImage };
      })
      .filter(({ taskId, generatedImage }) => {
        if (!taskId) return false;
        const existsInHistory = dedupedBaseHistory.some((item) => item.uuid === taskId);
        if (existsInHistory) return false;
        // 已完结反推条目不合成历史卡（数据隔离）：真实反推任务的完结记录
        // 由后端历史提供，这里只为进行中的反推任务保留过渡卡。
        const inspireMeta = currentInspireByTaskId.get(taskId);
        if (inspireMeta && !isActiveTaskStatus(mapInspireStatusToTaskStatus(inspireMeta.status))) {
          return false;
        }
        return shouldKeepCurrentTaskOverlay({
          currentStatus,
          hasGeneratedImage: !!generatedImage,
          wasSubmittedHere: submittedTaskIdsRef.current.has(taskId),
        });
      });

    if (missingCurrentTasks.length === 0) return filterByWorkflow(overlaid);

    const syntheticGroupId = currentTaskIds.length > 1 ? `active-${currentTaskIds.join("-")}` : "";
    const syntheticTasks: GenerateTask[] = missingCurrentTasks.map(({ taskId, index, generatedImage }) => {
      const inspireMeta = currentInspireByTaskId.get(taskId);
      const inspireStatus = mapInspireStatusToTaskStatus(inspireMeta?.status);
      const isFailed = failedIndexSet.has(index) || allCurrentTasksFailed || inspireStatus === TASK_STATUS.FAILED;
      const inputImages = inspireMeta?.inputImages?.filter(Boolean) || [];
      const thumbnail = generatedImage || inputImages[0] || "";
      const resolvedStatus = generatedImage
        ? TASK_STATUS.COMPLETED
        : isFailed
          ? TASK_STATUS.FAILED
          : inspireMeta
            ? inspireStatus
            : resolveHistoryDisplayStatus({
              originalStatus: TASK_STATUS.PENDING,
              currentStatus,
              hasGeneratedImage: !!generatedImage,
              isFailed,
            });
      return {
        uuid: taskId,
        group_id: syntheticGroupId,
        task_type: inspireMeta ? "inspire_reverse" : "",
        execution_kind: inspireMeta ? "inspire" : undefined,
        feature_key: inspireMeta ? "inspire_reverse" : "",
        title: inspireMeta ? (inspireMeta.presetLabel || "反推") : "",
        thumbnail,
        input_images: inputImages,
        image_count: 1,
        status: resolvedStatus,
        progress: Math.max(
          taskProgresses[index] || 0,
          currentProgress || 0,
          progressForStatus(resolvedStatus, undefined),
          generatedImage || isFailed ? 100 : 0,
        ),
        points_cost: 0,
        prompt: inspireMeta?.prompt || "",
        inspire_prompt: inspireMeta?.prompt || "",
        created_at: now,
        completed_at: generatedImage ? now : "",
        batch_index: currentTaskIds.length > 1 ? (inspireMeta?.batchIndex || index + 1) : inspireMeta?.batchIndex,
      };
    });

    return filterByWorkflow([...syntheticTasks, ...overlaid]);
  })();

  // 将任务列表按 group_id 分组
  const historyGroups: HistoryGroup[] = (() => {
    const groupMap = new Map<string, GenerateTask[]>();
    const soloTasks: GenerateTask[] = [];

    for (const item of displayHistory) {
      if (item.group_id) {
        const existing = groupMap.get(item.group_id);
        if (existing) existing.push(item);
        else groupMap.set(item.group_id, [item]);
      } else {
        soloTasks.push(item);
      }
    }

    const groups: HistoryGroup[] = [];

    // 组内按 batch_index 排序，保证提交顺序一致
    groupMap.forEach((tasks, groupId) => {
      const hasBatchIndex = tasks.some(t => (t.batch_index || 0) > 0);
      if (hasBatchIndex) {
        tasks.sort((a, b) => (a.batch_index || 0) - (b.batch_index || 0));
      }
      const first = tasks[0];
      const minProgress = Math.min(...tasks.map((t: GenerateTask) => t.progress || 0));

      const batchParent = tasks.find((t: GenerateTask) => t.is_batch_parent);
      // Derive completion counts from polled children if available, else from API response
      let batchCompletedCount = batchParent?.batch_completed_count || 0;
      let batchFailedCount = batchParent?.batch_failed_count || 0;
      if (batchParent) {
        const children = batchChildrenMap[batchParent.uuid];
        if (children && children.length > 0) {
          batchCompletedCount = children.filter(c => c.status === 3).length;
          batchFailedCount = children.filter(c => c.status === 4).length;
        }
      }
      const hasActive = tasks.some((t: GenerateTask) => isActiveTaskStatus(t.status));
      const hasFailed = tasks.some((t: GenerateTask) => t.status === TASK_STATUS.FAILED);
      const hasCompleted = tasks.some((t: GenerateTask) => t.status === TASK_STATUS.COMPLETED);
      const allCompleted = tasks.every((t: GenerateTask) => t.status === TASK_STATUS.COMPLETED);
      const allFailed = tasks.every((t: GenerateTask) => t.status === TASK_STATUS.FAILED);
      const allSettled = tasks.every((t: GenerateTask) =>
        t.status === TASK_STATUS.COMPLETED || t.status === TASK_STATUS.FAILED || t.status === TASK_STATUS.CANCELLED
      );
      let status: number;
      if (batchParent) {
        status = inferHistoryStatusFromCounts({
          total: batchParent.batch_total || tasks.length,
          completed: batchCompletedCount,
          failed: batchFailedCount,
          fallbackStatus: batchParent.status || first.status,
        });
      } else if (allCompleted) {
        status = TASK_STATUS.COMPLETED;
      } else if (allFailed) {
        status = TASK_STATUS.FAILED;
      } else if (allSettled && hasCompleted && hasFailed) {
        status = PARTIAL_STATUS;
      } else if (allSettled && hasFailed) {
        status = TASK_STATUS.FAILED;
      } else if (hasActive) {
        status = tasks.some((t: GenerateTask) => t.status === TASK_STATUS.PROCESSING)
          ? TASK_STATUS.PROCESSING
          : tasks.some((t: GenerateTask) => t.status === TASK_STATUS.QUEUED)
            ? TASK_STATUS.QUEUED
            : TASK_STATUS.PENDING;
      } else {
        status = first.status;
      }
      const isIndexedBatchGroup = !batchParent && hasBatchIndex && tasks.length > 1;
      if (isIndexedBatchGroup) {
        batchCompletedCount = tasks.filter((t) => t.status === TASK_STATUS.COMPLETED).length;
        batchFailedCount = tasks.filter((t) => t.status === TASK_STATUS.FAILED).length;
      }
      groups.push({
        groupId,
        tasks,
        thumbnail: tasks.find((t: GenerateTask) => t.thumbnail?.trim())?.thumbnail || "",
        featureKey: first.feature_key || first.task_type,
        prompt: first.prompt || "",
        status,
        progress: minProgress,
        createdAt: first.created_at,
        imageCount: batchParent ? (batchParent.batch_total || tasks.length) : tasks.length,
        isBatchParent: !!batchParent,
        isBatchGroup: isIndexedBatchGroup,
        batchTotal: batchParent?.batch_total || (isIndexedBatchGroup ? tasks.length : undefined),
        batchCompletedCount,
        batchFailedCount,
      });
    });

    // 独立任务（无 group_id）各自成组
    for (const item of soloTasks) {
      let soloBatchCompleted = item.batch_completed_count || 0;
      let soloBatchFailed = item.batch_failed_count || 0;
      if (item.is_batch_parent) {
        const children = batchChildrenMap[item.uuid];
        if (children && children.length > 0) {
          soloBatchCompleted = children.filter(c => c.status === 3).length;
          soloBatchFailed = children.filter(c => c.status === 4).length;
        }
      }
      const soloStatus = item.is_batch_parent
        ? inferHistoryStatusFromCounts({
          total: item.batch_total || 1,
          completed: soloBatchCompleted,
          failed: soloBatchFailed,
          fallbackStatus: item.status,
        })
        : item.status;
      groups.push({
        groupId: "",
        tasks: [item],
        thumbnail: item.thumbnail?.trim() || "",
        featureKey: item.feature_key || item.task_type,
        prompt: item.prompt || "",
        status: soloStatus,
        progress: item.progress || 0,
        createdAt: item.created_at,
        imageCount: item.is_batch_parent ? (item.batch_total || 0) : 1,
        isBatchParent: item.is_batch_parent,
        isBatchGroup: false,
        batchTotal: item.batch_total,
        batchCompletedCount: soloBatchCompleted,
        batchFailedCount: soloBatchFailed,
      });
    }

    // 按第一个任务在原始列表中的位置排序（保持 id DESC）
    groups.sort((a, b) => {
      const aIdx = displayHistory.findIndex(h => h.uuid === a.tasks[0].uuid);
      const bIdx = displayHistory.findIndex(h => h.uuid === b.tasks[0].uuid);
      return aIdx - bIdx;
    });

    return groups;
  })();

  const handleThumbnailClick = (e: React.MouseEvent, item: GenerateTask, taskGroupId?: string) => {
    e.stopPropagation();
    setPreviewTask(item);
    setPreviewGroupId(taskGroupId || null);
  };

  const buildActiveTaskPayload = useCallback((group: HistoryGroup): EcomActiveHistoryTaskPayload => {
    const parentTask = group.tasks[0];
    const regenSet = new Set(regeneratingTaskUuids);
    const refineSet = new Set(refiningTaskUuids);

    if (group.isBatchParent) {
      const children = [...(batchChildrenMap[parentTask.uuid] || [])].sort((a, b) => a.batch_index - b.batch_index);
      const total = Math.max(group.batchTotal || 0, group.imageCount || 0, children.length, 1);
      const images = new Array(total).fill("");
      const childTaskIds = new Array(total).fill("");
      const childStatuses = new Array(total).fill(TASK_STATUS.PENDING);
      const taskProgresses = new Array(total).fill(group.progress || 0);

      children.forEach((child, fallbackIndex) => {
        const idx = child.batch_index > 0 ? child.batch_index - 1 : fallbackIndex;
        if (idx < 0 || idx >= total) return;
        const isTracked = regenSet.has(child.uuid) || refineSet.has(child.uuid);
        const status = isTracked ? TASK_STATUS.PROCESSING : child.status;
        childTaskIds[idx] = child.uuid;
        childStatuses[idx] = status;
        taskProgresses[idx] = progressForStatus(status, child.progress);
        // 结果槽规范值：优先 output_images 原图，thumbnail(200x200 Small) 仅作降级占位
        const slotImage = pickEcomResultSlotImage(child, TASK_STATUS.COMPLETED);
        if (slotImage) {
          images[idx] = slotImage;
        }
      });

      return {
        images,
        modelImage: "",
        productImage: "",
        featureKey: group.featureKey,
        childTaskIds,
        childStatuses,
        taskId: parentTask.uuid,
        progress: group.progress,
        taskProgresses,
        isBatchParent: true,
        createdAt: parentTask.created_at || group.createdAt,
      };
    }

    const hasBatchIndex = group.tasks.some((task) => (task.batch_index || 0) > 0);
    const total = hasBatchIndex
      ? Math.max(...group.tasks.map((task) => task.batch_index || 0), group.tasks.length)
      : Math.max(group.tasks.length, 1);
    const images = new Array(total).fill("");
    const childTaskIds = new Array(total).fill("");
    const childStatuses = new Array(total).fill(TASK_STATUS.PENDING);
    const taskProgresses = new Array(total).fill(group.progress || 0);

    group.tasks.forEach((task, fallbackIndex) => {
      const idx = hasBatchIndex && (task.batch_index || 0) > 0 ? (task.batch_index as number) - 1 : fallbackIndex;
      if (idx < 0 || idx >= total) return;
      const isTracked = regenSet.has(task.uuid) || refineSet.has(task.uuid);
      const status = isTracked ? TASK_STATUS.PROCESSING : task.status;
      childTaskIds[idx] = task.uuid;
      childStatuses[idx] = status;
      taskProgresses[idx] = progressForStatus(status, task.progress);
      // 结果槽规范值：完成态优先 output_images 原图；此前直接写 task.thumbnail
      // 导致重试任务从活跃/历史恢复后图片详情误用 200x200 Small 缩略图
      const slotImage = pickEcomResultSlotImage(task, TASK_STATUS.COMPLETED);
      if (slotImage) {
        images[idx] = slotImage;
      }
    });

    return {
      images,
      modelImage: "",
      productImage: "",
      featureKey: group.featureKey,
      childTaskIds: group.tasks.length > 1 ? childTaskIds : undefined,
      childStatuses,
      taskId: parentTask.uuid,
      progress: group.progress,
      taskProgresses,
      isBatchParent: false,
      createdAt: parentTask.created_at || group.createdAt,
    };
  }, [batchChildrenMap, refiningTaskUuids, regeneratingTaskUuids]);

  const buildHistoryGroupDescriptor = useCallback((group: HistoryGroup): HistoryGroupDescriptor => ({
    groupId: group.groupId,
    tasks: group.tasks.map((t) => ({
      uuid: t.uuid,
      feature_key: t.feature_key,
      task_type: t.task_type,
      execution_kind: t.execution_kind,
      source_task_id: t.source_task_id,
      source_image_index: t.source_image_index,
      title: t.title,
      prompt: t.prompt,
      status: t.status,
      batch_index: t.batch_index,
      thumbnail: t.thumbnail,
      input_images: t.input_images,
      created_at: t.created_at,
      completed_at: t.completed_at,
    })),
    thumbnail: group.thumbnail,
    featureKey: group.featureKey,
    prompt: group.prompt,
    isBatchParent: group.isBatchParent,
    batchTotal: group.batchTotal,
  }), []);

  const handleCopyPrompt = useCallback(async (e: React.MouseEvent, prompt: string, groupKey: string) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPromptId(groupKey);
      setTimeout(() => setCopiedPromptId(null), 1500);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = prompt;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopiedPromptId(groupKey);
      setTimeout(() => setCopiedPromptId(null), 1500);
    }
  }, []);

  const buildDownloadGroupLike = useCallback((group: HistoryGroup, urls?: string[]): HistoryDownloadGroupLike => ({
    groupId: group.groupId,
    thumbnail: group.thumbnail,
    isBatchParent: group.isBatchParent,
    tasks: group.tasks.map((task) => ({
      uuid: task.uuid,
      status: task.status,
      batch_index: task.batch_index,
      thumbnail: task.thumbnail,
      output_images: task.is_batch_parent && urls ? urls : undefined,
    })),
  }), []);

  const resolveHistoryDownloadUrls = useCallback(async (group: HistoryGroup): Promise<string[]> => {
    const fallbackUrls = collectHistoryDownloadUrls(buildDownloadGroupLike(group));
    const primaryTask = group.tasks[0];

    try {
      if (group.isBatchParent && !group.groupId) {
        const res = await getBatchTaskStatus(primaryTask.uuid);
        const detailUrls = [...(res.data?.children || [])]
          .sort((a, b) => (a.batch_index || 0) - (b.batch_index || 0))
          .flatMap((child) => child.output_images || [])
          .filter(Boolean);
        if (detailUrls.length > 0) return detailUrls;
      } else if (group.isBatchParent || group.groupId) {
        const res = await ecomImageApi.getTaskGroup(group.groupId || primaryTask.group_id || primaryTask.uuid);
        const detailUrls = (res.data?.output_images || []).filter(Boolean);
        if (detailUrls.length > 0) return detailUrls;
      } else if (primaryTask?.uuid) {
        const detail = await ecomImageApi.getTask(primaryTask.uuid);
        const detailUrls = resolveTaskOutputImages(
          detail.data?.output_images,
          detail.data?.coze_raw_output,
        );
        if (detailUrls.length > 0) return detailUrls;
      }
    } catch (err) {
      console.warn("Failed to resolve full history download images, fallback to list data:", err);
    }

    return fallbackUrls;
  }, [buildDownloadGroupLike]);

  const handleDownloadHistoryGroup = useCallback(async (e: React.MouseEvent, group: HistoryGroup, groupKey: string) => {
    e.stopPropagation();
    if (downloadingIds.has(groupKey)) return;
    const primaryTask = group.tasks[0];
    if (!primaryTask?.uuid) return;

    setDownloadingIds(prev => new Set(prev).add(groupKey));
    try {
      const urls = await resolveHistoryDownloadUrls(group);
      const downloadImageCount = resolveHistoryDownloadImageCount(urls.length, group.imageCount);
      const mode = chooseHistoryDownloadMode(downloadImageCount);
      if (mode === "none") {
        feedback.warning("暂无可下载图片");
        return;
      }

      const prefix = `flowx-${primaryTask.uuid.slice(0, 8)}`;
      if (mode === "archive") {
        const blob = await ecomImageApi.downloadTaskArchive(primaryTask.uuid);
        downloadBlob(blob, `${prefix}.zip`);
        feedback.success(`已打包下载 ${downloadImageCount} 张图片`);
        return;
      }

      for (let i = 0; i < urls.length; i++) {
        const suffix = urls.length > 1 ? `-${String(i + 1).padStart(2, "0")}` : "";
        await downloadFile(urls[i], `${prefix}${suffix}.png`);
      }
      feedback.success(urls.length > 1 ? `已开始下载 ${urls.length} 张图片` : "已开始下载图片");
    } catch (err) {
      if (isDownloadCancelError(err)) return;
      console.error("Failed to download history images:", err);
      feedback.error({ code: "E9002", fallback: "operation" });
    } finally {
      setDownloadingIds(prev => {
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
    }
  }, [downloadingIds, resolveHistoryDownloadUrls]);

  const handleDeleteTask = useCallback(async (e: React.MouseEvent, group: HistoryGroup) => {
    e.stopPropagation();
    const taskUuid = group.tasks[0].uuid;
    setDeletingIds(prev => new Set(prev).add(taskUuid));
    try {
      await ecomImageApi.deleteTask(taskUuid);
      // 删除后保留当前已加载的页范围，避免把用户滚到下方查看的内容覆盖掉。
      await refreshLoadedHistory();
    } catch (err) {
      console.error("Failed to delete task:", err);
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(taskUuid);
        return next;
      });
    }
  }, [refreshLoadedHistory]);


  const featureKeyToName = (featureKey: string, fallbackTitle: string): string => {
    const match = workflowOptions.find(o => o.feature_key === featureKey);
    if (match) return match.name;
    return fallbackTitle;
  };

  const isTextSeparationHistoryGroup = (group: HistoryGroup, workflowName: string): boolean => {
    const fields = [
      workflowName,
      group.featureKey,
      group.prompt,
      ...group.tasks.flatMap((task) => [
        task.title,
        task.feature_key,
        task.task_type,
        task.execution_kind,
      ]),
    ]
      .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      .join(" ");

    return TEXT_SEPARATION_RECORD_RE.test(fields);
  };

  // 历史筛选下拉直接列出全部工作流（含当前工作流的真实名称），
  // 选中哪个工作流就高亮哪个，不再插入「当前工作流」占位项。
  const allFilterOptions: WorkflowOption[] = useMemo(() => [
    { feature_key: "all", name: "全部工作流" },
    ...workflowOptions,
  ], [workflowOptions]);

  const currentFilterName = allFilterOptions.find(o => o.feature_key === activeFilterKey)?.name || "全部工作流";
  const isPageVariant = variant === "page";
  const displayHistoryGroups: Array<HistoryGroup | { conversation: AgentConversation; createdAt: string }> = [
    ...historyGroups,
    ...agentHistory.map((conversation) => ({
      conversation,
      createdAt: conversation.updatedAt || conversation.createdAt || "",
    })),
  ].sort((a, b) => getHistoryTimestamp(b.createdAt) - getHistoryTimestamp(a.createdAt)
    || ("conversation" in a ? `agent:${a.conversation.conversationId}` : `task:${a.tasks[0].uuid}`)
      .localeCompare("conversation" in b ? `agent:${b.conversation.conversationId}` : `task:${b.tasks[0].uuid}`));

  return (
    <div
      style={{
        backgroundColor: "#0D0E11",
      }}
      className={[
        isPageVariant
          ? "h-full w-full min-w-0 rounded-[18px] border border-white/8 bg-[#0D0E11] flex flex-col overflow-hidden"
          : "w-70 h-full border-l border-white/8 flex flex-col overflow-hidden",
        className,
      ].filter(Boolean).join(" ")}
    >
      {/* Header */}
      <div className={`${isPageVariant ? "px-5 pt-5 pb-4" : "px-3 pt-4 pb-3"} flex items-center justify-between`}>
        <h3 className={`${isPageVariant ? "text-xl" : "text-sm"} font-semibold text-white`}>历史记录</h3>
        <div className="flex items-center gap-2">
          {isPageVariant && onCollapse && (
            <EcomButton
              type="button"
              onClick={onCollapse}
              aria-label="收起历史记录"
              title="收起历史记录"
              variant="ghost"
              size="mini"
              className="gap-1.5"
            >
              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M6 3H3.5A1.5 1.5 0 0 0 2 4.5v7A1.5 1.5 0 0 0 3.5 13H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <path d="M10 4l4 4-4 4M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              收起
            </EcomButton>
          )}
          {!isPageVariant && onExpand && (
            <EcomButton
              type="button"
              onClick={onExpand}
              aria-label="展开完整历史记录"
              title="展开完整历史记录"
              variant="ghost"
              size="mini"
              className="gap-1"
            >
              <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M6 3H3.5A1.5 1.5 0 0 0 2 4.5v7A1.5 1.5 0 0 0 3.5 13H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <path d="M10.5 5L7.5 8l3 3M8 8h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              展开
            </EcomButton>
          )}
          <EcomDropdown
            open={showFilterDropdown}
            onOpenChange={setShowFilterDropdown}
            placement="bottomRight"
            popup={(
              <div className="ecom-history-filter-popup" role="listbox" aria-label="筛选历史工作流">
                {allFilterOptions.map((opt) => (
                  <button
                    key={opt.feature_key}
                    type="button"
                    role="option"
                    aria-selected={activeFilterKey === opt.feature_key}
                    data-selected={activeFilterKey === opt.feature_key ? "true" : undefined}
                    onClick={() => {
                      setActiveFilterKey(opt.feature_key);
                      setShowFilterDropdown(false);
                    }}
                    className="ecom-history-filter-option"
                  >
                    {opt.name}
                  </button>
                ))}
              </div>
            )}
          >
            <EcomButton
              type="button"
              data-testid="history-filter-toggle"
              variant="ghost"
              size="mini"
              className="max-w-[140px]"
            >
              <span className="truncate">{currentFilterName}</span>
              <svg
                className={`w-3 h-3 shrink-0 transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                viewBox="0 0 12 12" fill="none"
              >
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </EcomButton>
          </EcomDropdown>
        </div>
      </div>

      {/* History List */}
      <div
        ref={listScrollRef}
        className={`${isPageVariant ? "px-5 pb-5" : "px-3 pb-3"} min-w-0 flex-1 overflow-x-hidden overflow-y-auto [&::-webkit-scrollbar]:hidden will-change-transform transform-[translateZ(0)]`}
        style={{ scrollbarWidth: "none" }}
      >
        {agentError && <div role="alert" className="mb-2 flex items-center justify-between gap-2 text-xs text-white/55">
          <span>智能主图历史暂不可用</span>
          <EcomButton size="small" variant="outline" onClick={() => setAgentRetryKey((value) => value + 1)}>重试</EcomButton>
        </div>}
        {(loading || agentLoading) && displayHistoryGroups.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
            <span className="ml-2 text-xs text-white/40">加载中...</span>
          </div>
        ) : displayHistoryGroups.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-white/40 text-xs gap-1">
            <svg className="w-8 h-8 text-white/15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>暂无生成记录</span>
            {archivedThrough && <span>{archivedThrough} 及之前的生成记录已归档，如需查看请联系管理员恢复</span>}
          </div>
        ) : (
          <>
            <div className={isPageVariant ? "space-y-5" : "space-y-2"}>
              {displayHistoryGroups.map((group, groupIndex) => {
                const previousGroup = displayHistoryGroups[groupIndex - 1];
                const showDateHeader = !previousGroup
                  || getHistoryDateKey(previousGroup.createdAt) !== getHistoryDateKey(group.createdAt);
                if ("conversation" in group) {
                  return <div key={`agent:${group.conversation.conversationId}`}>
                    {showDateHeader && <div className={`mb-2 font-semibold text-white/90 ${isPageVariant ? "text-[18px]" : "text-xs"}`}>
                      {formatHistoryDateLabel(group.createdAt)}
                    </div>}
                    <AgentHistoryCard conversation={group.conversation} variant={variant}
                      workflowName={featureKeyToName(group.conversation.moduleKey, "工作流")}
                      timeLabel={formatTime(group.createdAt)}
                      onOpen={(id) => { void openAgentConversation(id); }}
                      busy={!!openingConversationId} />
                  </div>;
                }
                const regenSet = new Set(regeneratingTaskUuids);
                const refineSet = new Set(refiningTaskUuids);
                // Only show "editing" status for groups that actually contain the refining/regenerating task
                // Check against the group's own task UUIDs, not just any task in the list
                const groupTaskUuids = group.isBatchParent
                  ? (batchChildrenMap[group.tasks[0].uuid] || []).map(c => c.uuid)
                  : group.tasks.map(t => t.uuid);
                const hasRegenChild = groupTaskUuids.some(uuid => regenSet.has(uuid));
                const hasRefineChild = groupTaskUuids.some(uuid => refineSet.has(uuid));
                const cfg = hasRegenChild
                  ? { label: "重新生成中", color: "text-purple-400", bg: "bg-purple-500/15" }
                  : hasRefineChild
                    ? { label: "编辑中", color: "text-amber-400", bg: "bg-amber-500/15" }
                    : (STATUS_CONFIG[group.status] || STATUS_CONFIG[TASK_STATUS.PENDING]);
                const isCompleted = group.status === TASK_STATUS.COMPLETED;
                const isFailed = group.status === TASK_STATUS.FAILED;
                const isActive = isActiveTaskStatus(group.status) || hasRegenChild || hasRefineChild;
                const isInspireGroup = group.tasks.some(t => t.execution_kind === "inspire");
                const hasCompletedTask = group.tasks.some(t => t.status === TASK_STATUS.COMPLETED);
                const isClickable =
                  hasCompletedTask || isCompleted || isFailed || !!(group.thumbnail || "").trim() || isActive;
                const isMulti = group.imageCount > 1;
                const isExpanded = expandedGroupId === (group.groupId || group.tasks[0].uuid);
                const groupKey = group.groupId || group.tasks[0].uuid;
                const groupSlotId = groupKey;
                const groupThumbSrc = (group.thumbnail || "").trim();
                const groupThumbFailed =
                  !!groupThumbSrc && !!failedThumbs[thumbFailMapKey(groupSlotId, groupThumbSrc)];
                const thumbnailStack = collectHistoryThumbnailStack({
                  primaryThumbnail: groupThumbSrc,
                  tasks: group.tasks,
                  batchChildren: group.isBatchParent
                    ? batchChildrenMap[group.tasks[0].uuid]
                    : undefined,
                });
                const rearThumbnails = thumbnailStack
                  .slice(1)
                  .filter((url) => !failedThumbs[thumbFailMapKey(groupSlotId, url)])
                  .slice(0, 2);
                const hasThumbnailStack = isPageVariant && !groupThumbFailed && rearThumbnails.length > 0;
                const THUMBNAIL_STACK_STEP = 8;
                const thumbnailStackOffset = hasThumbnailStack ? rearThumbnails.length * THUMBNAIL_STACK_STEP : 0;
                const thumbnailWidth = isPageVariant ? 68 : 48;
                const thumbnailHeight = portraitThumbs.has(groupThumbSrc)
                  ? isPageVariant ? 102 : 72
                  : thumbnailWidth;
                const thumbnailRadius = 4;
                const workflowName = group.isBatchParent
                  ? group.tasks[0].title || `批处理 (${group.batchTotal}张)`
                  : featureKeyToName(group.featureKey, group.tasks[0].title || group.featureKey || "生成任务");
                const isTextSeparationRecord = isTextSeparationHistoryGroup(group, workflowName);
                const isImageLayerRecord = group.tasks.some((task) =>
                  task.task_type === "seedream_layer" || task.feature_key === "seedream_layer" || task.title === "图层分离" || task.title === "文字分离",
                );
                const canViewImageLayer = isImageLayerRecord && !isActive && !!onViewImageLayer;
                const isOpeningImageLayer = openingImageLayerId === group.tasks[0].uuid;
                const isAgentRecord = group.tasks.some(task => task.execution_kind === "agent_tool"
                  || task.task_type === "agent_workflow" || task.feature_key === "agent");
                const statusChip = cfg.label === STATUS_CONFIG[TASK_STATUS.COMPLETED].label ? null : cfg;
                const completedAt = group.tasks
                  .map((task) => task.completed_at)
                  .filter(Boolean)
                  .sort((a, b) => getHistoryTimestamp(b) - getHistoryTimestamp(a))[0] || "";
                const pointsCost = group.tasks.reduce((sum, task) => sum + (task.points_cost || 0), 0);
                const imageMeta = groupThumbSrc ? historyImageMeta[groupThumbSrc] : undefined;
                const groupPromptText = extractPromptValue(group.prompt || "");
                const showPromptText = !!groupPromptText && !isTextSeparationRecord;
                const canRestoreAgent = isAgentRecord && !!onOpenAgentHistory;
                const canRegenerate = canRestoreAgent || (isPageVariant && !isTextSeparationRecord && !!onRegenerateFromHistory && (hasCompletedTask || !!groupThumbSrc));
                const canViewGenerating = !canRestoreAgent && isPageVariant && !isTextSeparationRecord && isActive && !!onActiveTaskClick;
                const knownDownloadUrls = collectHistoryDownloadUrls(buildDownloadGroupLike(group));
                const canDownloadHistory = !isActive && knownDownloadUrls.length > 0 && (hasCompletedTask || isCompleted || group.status === PARTIAL_STATUS);
                const isDownloadingHistory = downloadingIds.has(groupKey);
                const showPageActions = canRestoreAgent || (isPageVariant && (canRegenerate || canViewGenerating || canDownloadHistory || showPromptText));
                const isPromptCopied = copiedPromptId === groupKey;

                return (
                  <div
                    key={groupKey}
                    className="history-record-item min-w-0"
                  >
                    {showDateHeader && (
                      <div className={`mb-2 font-semibold text-white/90 ${isPageVariant ? "text-[18px]" : "text-xs"}`}>
                        {formatHistoryDateLabel(group.createdAt)}
                      </div>
                    )}
                    <div
                      className={`w-full min-w-0 flex items-start transition-all text-left relative group/item ${isPageVariant
                        ? "cursor-default"
                        : "cursor-default"
                        } ${isPageVariant
                          ? "gap-4 rounded-xl border-white/6 px-3.5 py-3 shadow-[0_8px_24px_rgba(0,0,0,0.18)]"
                          : "gap-2.5 p-2 rounded-lg"
                        }`}
                    >
                      {/* Thumbnail */}
                      <div
                        className={`shrink-0 relative group ${(groupThumbSrc || isTextSeparationRecord) && isClickable ? "cursor-pointer" : ""}`}
                        data-history-thumbnail-stack={hasThumbnailStack ? rearThumbnails.length + 1 : 1}
                        style={{
                          width: thumbnailWidth + thumbnailStackOffset,
                          height: thumbnailHeight + thumbnailStackOffset,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (groupThumbSrc && isClickable) {
                            if (isInspireGroup) {
                              setDetailGroup(buildHistoryGroupDescriptor(group));
                              setDetailModalOpen(true);
                              return;
                            }
                            const targetTask = group.tasks.find(t => t.status === TASK_STATUS.COMPLETED) || group.tasks.find(t => t.status === TASK_STATUS.FAILED) || group.tasks[0];
                            handleThumbnailClick(e, targetTask, (group.isBatchParent || group.tasks.length > 1) ? group.groupId : undefined);
                          }
                        }}
                      >
                        {hasThumbnailStack && rearThumbnails.map((url, index) => {
                          const layerOffset = (rearThumbnails.length - index - 1) * THUMBNAIL_STACK_STEP;
                          return (
                            <div
                              key={url}
                              className="absolute overflow-hidden border border-black/40 bg-[#17181B] shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
                              data-history-thumbnail-layer="rear"
                              style={{
                                left: layerOffset,
                                top: layerOffset,
                                width: thumbnailWidth,
                                height: thumbnailHeight,
                                borderRadius: thumbnailRadius,
                                zIndex: rearThumbnails.length - index,
                              }}
                              aria-hidden="true"
                            >
                              <img
                                src={url}
                                alt=""
                                className="h-full w-full object-cover"
                                decoding="async"
                                loading="lazy"
                                onError={() => markThumbFailed(groupSlotId, url)}
                              />
                              <div className="absolute inset-0 bg-black/45" />
                            </div>
                          );
                        })}
                        <div
                          className={`absolute bottom-0 right-0 overflow-hidden bg-white/5 ${hasThumbnailStack ? "border border-black/40 shadow-[0_2px_8px_rgba(0,0,0,0.35)]" : ""}`}
                          data-history-thumbnail-layer="front"
                          style={{
                            width: thumbnailWidth,
                            height: thumbnailHeight,
                            borderRadius: thumbnailRadius,
                            zIndex: rearThumbnails.length + 1,
                          }}
                        >
                          {groupThumbSrc && !groupThumbFailed ? (
                            <>
                              <img
                                key={thumbFailMapKey(groupSlotId, groupThumbSrc)}
                                onLoad={(e) => {
                                  handleThumbLoad(e, groupThumbSrc);
                                  clearThumbFailed(groupSlotId, groupThumbSrc);
                                }}
                                onError={() => markThumbFailed(groupSlotId, groupThumbSrc)}
                                src={groupThumbSrc}
                                alt=""
                                className="w-full h-full object-cover"
                                decoding="async"
                                loading="lazy"
                              />
                              {isMulti && (
                                <div className="absolute top-0.5 right-0.5 bg-black/70 text-white text-[9px] px-1 py-0.5 rounded leading-none">
                                  {group.imageCount}张
                                </div>
                              )}
                              {isClickable && (
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100
                                                flex items-center justify-center transition-opacity">
                                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </div>
                              )}
                            </>
                          ) : isActive ? (
                            <div className="w-full h-full flex items-center justify-center bg-linear-to-br from-blue-500/20 to-purple-500/20">
                              <div className="w-5 h-5 border-2 border-blue-400/40 border-t-blue-400 rounded-full animate-spin" />
                            </div>
                          ) : isFailed ? (
                            <div className="w-full h-full flex items-center justify-center bg-red-500/5 ring-1 ring-red-500/30 rounded-md">
                              <svg className="w-5 h-5 text-red-400/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white/15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        {isPageVariant ? (
                          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
                            <span className="shrink-0 text-[13px] font-medium text-[#00FAE5]">
                              {workflowName}
                            </span>
                            {statusChip && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusChip.bg} ${statusChip.color} ${isActive ? "animate-pulse" : ""}`}>
                                {statusChip.label}
                              </span>
                            )}
                            {group.tasks[0].is_retry && (
                              <span className="shrink-0 px-1.5 py-0.5 bg-orange-500/15 text-orange-400 text-[10px] rounded">
                                重试
                              </span>
                            )}
                            {(group.isBatchParent || group.isBatchGroup) && (
                              <span className="shrink-0 px-1.5 py-0.5 bg-purple-500/15 text-purple-400 text-[10px] rounded">
                                批处理
                              </span>
                            )}
                            {(group.isBatchParent || group.isBatchGroup) && group.batchTotal && group.batchTotal > 0 && (
                              <span className={`text-[10px] ${isActive ? "text-emerald-400/80" : "text-white/40"}`}>
                                {isActive
                                  ? `${group.batchCompletedCount || 0}/${group.batchTotal} 已完成`
                                  : `${group.batchTotal}张`}
                                {isActive && (group.batchFailedCount || 0) > 0 && (
                                  <span className="text-red-400/80 ml-0.5">{group.batchFailedCount}失败</span>
                                )}
                              </span>
                            )}
                            {imageMeta?.dimensions && (
                              <HistoryMetaField label="尺寸">
                                {formatDimensionLabel(imageMeta.dimensions)}
                              </HistoryMetaField>
                            )}
                            {imageMeta?.fileSize && (
                              <HistoryMetaField label="文件大小">
                                {imageMeta.fileSize}
                              </HistoryMetaField>
                            )}
                            {group.createdAt && (
                              <HistoryMetaField label="生成时间">
                                {formatHistoryRecordTime(group.createdAt)}
                              </HistoryMetaField>
                            )}
                            {completedAt && (
                              <HistoryMetaField label="完成时间">
                                {formatHistoryRecordTime(completedAt)}
                              </HistoryMetaField>
                            )}
                            {pointsCost > 0 && (
                              <HistoryMetaField label="消耗豌豆">
                                <DouziAmount amount={pointsCost} className="text-[12px]" />
                              </HistoryMetaField>
                            )}
                            {(isMulti || group.isBatchParent) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedGroupId(isExpanded ? null : groupKey);
                                  if (group.isBatchParent && !isExpanded) {
                                    fetchBatchChildren(group.tasks[0].uuid);
                                  }
                                }}
                                aria-label={isExpanded ? "收起子任务列表" : "展开子任务列表"}
                                title={isExpanded ? "收起子任务列表" : "展开子任务列表"}
                                className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-white/10 transition-colors"
                              >
                                <svg
                                  className={`w-3.5 h-3.5 text-white/50 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                  viewBox="0 0 12 12" fill="none"
                                >
                                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            {group.tasks[0].is_retry && (
                              <span className="shrink-0 px-1 py-0.5 bg-orange-500/20 text-orange-400 text-[9px] rounded font-medium">
                                重试
                              </span>
                            )}
                            {(group.isBatchParent || group.isBatchGroup) && (
                              <span className="shrink-0 px-1 py-0.5 bg-purple-500/20 text-purple-400 text-[9px] rounded font-medium">
                                批处理
                              </span>
                            )}
                            <span className="min-w-0 flex-1 text-xs text-white/90 truncate">
                              {workflowName}
                            </span>
                            {(isMulti || group.isBatchParent) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedGroupId(isExpanded ? null : groupKey);
                                  if (group.isBatchParent && !isExpanded) {
                                    fetchBatchChildren(group.tasks[0].uuid);
                                  }
                                }}
                                aria-label={isExpanded ? "收起子任务列表" : "展开子任务列表"}
                                title={isExpanded ? "收起子任务列表" : "展开子任务列表"}
                                className="shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-white/10 transition-colors"
                              >
                                <svg
                                  className={`w-3 h-3 text-white/50 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                  viewBox="0 0 12 12" fill="none"
                                >
                                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            )}
                            {canDownloadHistory && (
                              <button
                                type="button"
                                onClick={(e) => handleDownloadHistoryGroup(e, group, groupKey)}
                                disabled={isDownloadingHistory}
                                className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-white/6 text-white/45 transition-all hover:bg-[#00FAE5]/10 hover:text-[#00FAE5] disabled:cursor-wait disabled:text-[#00FAE5]/70"
                                title={isDownloadingHistory ? "下载中" : "下载图片"}
                                aria-label={isDownloadingHistory ? "下载中" : "下载图片"}
                              >
                                <HistoryDownloadIcon spinning={isDownloadingHistory} sizeClass="h-3 w-3" />
                              </button>
                            )}
                            {canViewImageLayer && (
                              <EcomButton
                                type="button"
                                variant="outline"
                                size="small"
                                onClick={(e) => { e.stopPropagation(); void handleViewImageLayer(group); }}
                                disabled={isOpeningImageLayer}
                                className="shrink-0"
                              >
                                {isOpeningImageLayer ? "加载中" : "查看分层"}
                              </EcomButton>
                            )}
                          </div>
                        )}
                        {showPromptText && (() => {
                          return isPageVariant ? (
                            <div
                              data-history-prompt
                              className="mt-2 min-w-0 max-w-full rounded-md border border-white/5 bg-[#1E1E21] px-3 py-2.5 text-[13px] leading-relaxed text-[#F6F6F6] break-words whitespace-pre-wrap"
                              style={{ overflowWrap: "anywhere" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {groupPromptText}
                            </div>
                          ) : (
                            <div
                              className="mt-1 flex items-center gap-1 rounded-md bg-white/4 px-1.5 py-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="min-w-0 flex-1 truncate text-[10px] text-white/45" title={groupPromptText}>
                                {groupPromptText}
                              </div>
                              <CopyPromptButton
                                copied={copiedPromptId === groupKey}
                                onClick={(e) => handleCopyPrompt(e, groupPromptText, groupKey)}
                                size="xs"
                              />
                            </div>
                          );
                        })()}
                        {showPageActions && (
                          <div className="mt-2 flex items-center gap-2 justify-start">
                            {canViewImageLayer && (
                              <EcomButton
                                type="button"
                                variant="outline"
                                size="small"
                                onClick={(e) => { e.stopPropagation(); void handleViewImageLayer(group); }}
                                disabled={isOpeningImageLayer}
                              >
                                {isOpeningImageLayer ? "加载中" : "查看分层"}
                              </EcomButton>
                            )}
                            {canRegenerate && (
                              <EcomButton
                                type="button"
                                variant="default"
                                size="small"
                                disabled={canRestoreAgent && !!restoringAgentTaskId}
                                aria-busy={canRestoreAgent && restoringAgentTaskId === group.tasks[0].uuid}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (canRestoreAgent) {
                                    void onOpenAgentHistory?.(group.tasks[0].uuid);
                                    return;
                                  }
                                  setDetailGroup(buildHistoryGroupDescriptor(group));
                                  setDetailModalOpen(true);
                                }}
                                className="gap-1!"
                              >
                                <HistoryRegenerateIcon spinning={canRestoreAgent && restoringAgentTaskId === group.tasks[0].uuid} />
                                {canRestoreAgent ? "恢复对话" : "重新生成"}
                              </EcomButton>
                            )}
                            {canViewGenerating && (
                              <EcomButton
                                type="button"
                                variant="default"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onActiveTaskClick(buildActiveTaskPayload(group));
                                }}
                                className="gap-1!"
                              >
                                <HistoryRegenerateIcon spinning />
                                生成中
                              </EcomButton>
                            )}
                            {canDownloadHistory && (
                              <EcomButton
                                type="button"
                                variant="outline"
                                size="small"
                                onClick={(e) => handleDownloadHistoryGroup(e, group, groupKey)}
                                disabled={isDownloadingHistory}
                                className="gap-1!"
                              >
                                <HistoryDownloadIcon spinning={isDownloadingHistory} />
                                {isDownloadingHistory ? "下载中" : "下载"}
                              </EcomButton>
                            )}
                            {showPromptText && (
                              <EcomButton
                                type="button"
                                variant="outline"
                                size="small"
                                onClick={(e) => handleCopyPrompt(e, groupPromptText, groupKey)}
                                className="gap-1!"
                                title={isPromptCopied ? "已复制" : "复制提示词"}
                                aria-label={isPromptCopied ? "已复制提示词" : "复制提示词"}
                              >
                                {isPromptCopied ? (
                                  <svg
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                  >
                                    <path
                                      d="M4 8.5l2.5 2.5L12 5"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                  >
                                    <rect
                                      x="5.5"
                                      y="5.5"
                                      width="8"
                                      height="8"
                                      rx="1.5"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                    />
                                    <path
                                      d="M3.5 10.5V4.5A1.5 1.5 0 0 1 5 3h6"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                )}
                                {isPromptCopied ? "已复制" : "复制"}
                              </EcomButton>
                            )}
                          </div>
                        )}
                        {!isPageVariant && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} ${isActive ? "animate-pulse" : ""}`}>
                              {cfg.label}
                            </span>
                            {(group.isBatchParent || group.isBatchGroup) && isActive && group.batchTotal && group.batchTotal > 0 && (
                              <span className="text-[10px] text-emerald-400/80">
                                {(group.batchCompletedCount || 0)}/{group.batchTotal} 已完成
                                {(group.batchFailedCount || 0) > 0 && (
                                  <span className="text-red-400/80 ml-0.5">{group.batchFailedCount}失败</span>
                                )}
                              </span>
                            )}
                            {/* Show completion summary for finished batch parents */}
                            {(group.isBatchParent || group.isBatchGroup) && !isActive && group.batchTotal && group.batchTotal > 0 && (
                              <span className="text-[10px] text-white/35">
                                {group.batchTotal}张
                              </span>
                            )}
                            {!((group.isBatchParent || group.isBatchGroup) && group.batchTotal && group.batchTotal > 0) && (
                              <span className="text-[10px] text-white/30">
                                {formatTime(group.createdAt)}
                              </span>
                            )}
                          </div>
                        )}
                        {group.tasks[0].is_retry && group.tasks[0].retry_source_id && (
                          <div className="text-[10px] text-white/30 truncate" title={`来源任务: ${group.tasks[0].retry_source_id}`}>
                            来源: #{group.tasks[0].retry_source_id.slice(0, 8)}
                          </div>
                        )}
                        {(isFailed || group.status === TASK_STATUS.CANCELLED) && (
                          <button
                            onClick={(e) => handleDeleteTask(e, group)}
                            disabled={deletingIds.has(group.tasks[0].uuid)}
                            className="absolute top-1.5 right-1 w-6 h-6 flex items-center justify-center rounded-md
                                       opacity-0 group-hover/item:opacity-100 transition-all
                                       bg-red-500/10 hover:bg-red-500/20 text-red-400/60 hover:text-red-400
                                       disabled:opacity-50 disabled:cursor-not-allowed"
                            title="删除此记录"
                          >
                            {deletingIds.has(group.tasks[0].uuid) ? (
                              <div className="w-3 h-3 border border-red-400/40 border-t-red-400 rounded-full animate-spin" />
                            ) : (
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                        )}
                        {isActive && (() => {
                          const hasCurrentTask = group.tasks.some(t => currentTaskIds.includes(t.uuid));
                          const displayProgress = hasCurrentTask
                            ? Math.max(currentProgress, group.progress || 0)
                            : group.progress || 5;
                          return (
                            <div className="mt-1.5 h-1 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${Math.min(Math.max(displayProgress, 5), 100)}%` }}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* 展开的批处理子任务列表 */}
                    {group.isBatchParent && isExpanded && (() => {
                      const children = [...(batchChildrenMap[group.tasks[0].uuid] || [])].sort((a, b) => a.batch_index - b.batch_index);
                      const regenSet = new Set(regeneratingTaskUuids);
                      const refineSet = new Set(refiningTaskUuids);
                      const BATCH_STATUS_CONFIG: Record<number, { label: string; bg: string; color: string }> = {
                        1: { label: "等待中", bg: "bg-yellow-500/10", color: "text-yellow-400" },
                        2: { label: "处理中", bg: "bg-blue-500/10", color: "text-blue-400" },
                        3: { label: "已完成", bg: "bg-green-500/10", color: "text-green-400" },
                        4: { label: "失败", bg: "bg-red-500/10", color: "text-red-400" },
                        5: { label: "已取消", bg: "bg-gray-500/10", color: "text-gray-400" },
                        6: { label: "排队中", bg: "bg-white/5", color: "text-white/40" },
                      };
                      const REGEN_CFG = { label: "重新生成中", bg: "bg-purple-500/15", color: "text-purple-400" };
                      const REFINE_CFG = { label: "编辑中", bg: "bg-amber-500/15", color: "text-amber-400" };
                      return children.length === 0 ? (
                        <div className="ml-1 mr-1 mb-1 mt-0.5 text-center py-3 text-[10px] text-white/30">
                          <div className="w-4 h-4 border-2 border-white/20 border-t-white/50 rounded-full animate-spin mx-auto mb-1" />
                          加载子任务...
                        </div>
                      ) : (
                        <div className="ml-1 mr-1 mb-1 mt-0.5 space-y-0.5 max-h-75 overflow-y-auto">
                          {children.map((child) => {
                            const isRegenerating = regenSet.has(child.uuid);
                            const isRefining = refineSet.has(child.uuid);
                            const bCfg = isRegenerating ? REGEN_CFG : isRefining ? REFINE_CFG : (BATCH_STATUS_CONFIG[child.status] || BATCH_STATUS_CONFIG[6]);
                            const isChildActive = child.status === 1 || child.status === 2 || isRegenerating || isRefining;
                            const childThumbSrc = (child.output_images?.[0] || child.thumbnail || "").trim();
                            const childSlot = `bc-${child.uuid}`;
                            const childThumbFailed =
                              !!childThumbSrc && !!failedThumbs[thumbFailMapKey(childSlot, childThumbSrc)];
                            const childPromptText = extractPromptValue(child.prompt || group.prompt || "");
                            const childPromptKey = `${groupKey}:child:${child.uuid}`;
                            return (
                              <div
                                key={child.uuid}
                                className="flex items-start gap-2 p-1.5 rounded-md transition-all cursor-default"
                              >
                                <div
                                  className="shrink-0 overflow-hidden bg-white/5 relative"
                                  data-history-subtask-thumbnail="batch"
                                  style={{ width: thumbnailWidth, height: thumbnailHeight, borderRadius: thumbnailRadius }}
                                >
                                  {child.status === 3 && childThumbSrc && !childThumbFailed ? (
                                    <img
                                      key={thumbFailMapKey(childSlot, childThumbSrc)}
                                      onLoad={() => clearThumbFailed(childSlot, childThumbSrc)}
                                      onError={() => markThumbFailed(childSlot, childThumbSrc)}
                                      src={childThumbSrc}
                                      alt=""
                                      className="w-full h-full object-cover"
                                      decoding="async"
                                      loading="lazy"
                                    />
                                  ) : isRegenerating || child.status === 2 ? (
                                    <div className={`w-full h-full flex items-center justify-center ${isRegenerating ? "bg-linear-to-br from-purple-500/20 to-pink-500/20" : "bg-linear-to-br from-blue-500/20 to-purple-500/20"}`}>
                                      <div className={`w-4 h-4 border-2 rounded-full animate-spin ${isRegenerating ? "border-purple-400/40 border-t-purple-400" : "border-blue-400/40 border-t-blue-400"}`} />
                                    </div>
                                  ) : child.status === 4 ? (
                                    <div className="w-full h-full flex items-center justify-center bg-red-500/5 ring-1 ring-red-500/30">
                                      <svg className="w-3.5 h-3.5 text-red-400/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
                                      </svg>
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <svg className="w-3.5 h-3.5 text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" strokeLinecap="round" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] text-white/60 truncate">
                                    子任务 {child.batch_index}
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className={`text-[9px] px-1 py-px rounded ${bCfg.bg} ${bCfg.color} ${isChildActive ? "animate-pulse" : ""}`}>
                                      {isRegenerating ? REGEN_CFG.label : (child.status_label || bCfg.label)}
                                    </span>
                                    {(isRegenerating || child.status === 2) && child.progress > 0 && (
                                      <span className={`text-[9px] ${isRegenerating ? "text-purple-400/80" : "text-blue-400/80"}`}>AI Generating</span>
                                    )}
                                    {!isChildActive && child.completed_at && (
                                      <span className="text-[9px] text-white/25">{formatTime(child.completed_at)}</span>
                                    )}
                                  </div>
                                  {childPromptText && (
                                    <SubTaskPromptPreview
                                      prompt={childPromptText}
                                      copied={copiedPromptId === childPromptKey}
                                      onCopy={(e) => handleCopyPrompt(e, childPromptText, childPromptKey)}
                                    />
                                  )}
                                  {(isRegenerating || child.status === 2) && (
                                    <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                                      <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.max(child.progress, 3)}%`, background: isRegenerating ? "linear-gradient(90deg, #A855F7, #D24DDA)" : "linear-gradient(90deg, #6BBDFC, #D24DDA)" }} />
                                    </div>
                                  )}
                                  {child.status === 4 && !isRegenerating && (
                                    <div
                                      className="text-[9px] text-red-400/70 truncate mt-0.5"
                                      title={`${child.failure?.code || "E5002"} 生图失败，请重试`}
                                    >
                                      {child.failure?.code || "E5002"} 生图失败，请重试
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* 展开的子任务列表（普通批次） — 按 batch_index 排序 */}
                    {isMulti && !group.isBatchParent && isExpanded && (() => {
                      const sortedTasks = [...group.tasks].sort((a, b) => (a.batch_index || 0) - (b.batch_index || 0));
                      return (
                        <div className="ml-1 mr-1 mb-1 mt-0.5 space-y-0.5">
                          {sortedTasks.map((task, taskIdx) => {
                            const taskCompleted = task.status === TASK_STATUS.COMPLETED;
                            const taskFailed = task.status === TASK_STATUS.FAILED;
                            const isTaskRegen = regeneratingTaskUuids.includes(task.uuid) && isActiveTaskStatus(task.status);
                            const isTaskRefine = refiningTaskUuids.includes(task.uuid);
                            const taskIsActive = isActiveTaskStatus(task.status) || isTaskRegen || isTaskRefine;
                            const rowThumbSrc = (task.thumbnail || "").trim();
                            const rowSlot = `row-${task.uuid}`;
                            const rowThumbFailed =
                              !!rowThumbSrc && !!failedThumbs[thumbFailMapKey(rowSlot, rowThumbSrc)];
                            const taskHasContent = taskCompleted || (taskFailed && !!task.thumbnail);
                            const taskCfg = isTaskRegen
                              ? { label: "重新生成中", color: "text-purple-400", bg: "bg-purple-500/15" }
                              : isTaskRefine
                                ? { label: "编辑中", color: "text-amber-400", bg: "bg-amber-500/15" }
                                : (STATUS_CONFIG[task.status] || STATUS_CONFIG[TASK_STATUS.PENDING]);
                            const taskPromptText = extractPromptValue(task.prompt || group.prompt || "");
                            const taskPromptKey = `${groupKey}:task:${task.uuid}`;
                            const currentTaskIndex = currentTaskIds.indexOf(task.uuid);
                            const subTaskProgress = currentTaskIndex >= 0
                              ? Math.max(taskProgresses[currentTaskIndex] || 0, task.progress || 0)
                              : task.progress || 0;
                            return (
                              <div
                                key={task.uuid}
                                className={`flex items-start gap-2 p-1.5 rounded-md transition-all cursor-default ${taskFailed ? "opacity-70" : ""}`}
                              >
                                <div
                                  className={`shrink-0 overflow-hidden bg-white/5 relative group/thumb ${taskFailed ? "ring-1 ring-red-500/40" : ""} ${taskHasContent && rowThumbSrc ? "cursor-pointer" : ""}`}
                                  data-history-subtask-thumbnail="row"
                                  style={{ width: thumbnailWidth, height: thumbnailHeight, borderRadius: thumbnailRadius }}
                                  onClick={(e) => {
                                    if (taskHasContent && rowThumbSrc) {
                                      e.stopPropagation();
                                      setPreviewTask(task);
                                      setPreviewGroupId(null);
                                    }
                                  }}
                                >
                                  {rowThumbSrc && !rowThumbFailed ? (
                                    <>
                                      <img
                                        key={thumbFailMapKey(rowSlot, rowThumbSrc)}
                                        onLoad={(e) => {
                                          handleThumbLoad(e, rowThumbSrc);
                                          clearThumbFailed(rowSlot, rowThumbSrc);
                                        }}
                                        onError={() => markThumbFailed(rowSlot, rowThumbSrc)}
                                        src={rowThumbSrc}
                                        alt=""
                                        className={`w-full h-full object-cover ${taskFailed ? "opacity-40" : ""}`}
                                        decoding="async"
                                        loading="lazy"
                                      />
                                      {taskHasContent && (
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100
                                                    flex items-center justify-center transition-opacity">
                                          <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </div>
                                      )}
                                    </>
                                  ) : taskIsActive ? (
                                    <div className={`w-full h-full flex items-center justify-center ${isTaskRegen ? "bg-linear-to-br from-purple-500/20 to-pink-500/20" : "bg-linear-to-br from-blue-500/20 to-purple-500/20"}`}>
                                      <div className={`w-4 h-4 border-2 rounded-full animate-spin ${isTaskRegen ? "border-purple-400/40 border-t-purple-400" : "border-blue-400/40 border-t-blue-400"}`} />
                                    </div>
                                  ) : taskFailed ? (
                                    <div className="w-full h-full flex items-center justify-center bg-red-500/5">
                                      <svg className="w-3.5 h-3.5 text-red-400/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
                                      </svg>
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <svg className="w-3.5 h-3.5 text-white/15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                      </svg>
                                    </div>
                                  )}
                                  {taskFailed && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-red-500/80 text-white text-[7px] text-center py-px leading-none">
                                      失败
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] text-white/60 truncate">
                                    子任务 {(task.batch_index || 0) > 0 ? task.batch_index : taskIdx + 1}
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className={`text-[9px] px-1 py-px rounded ${taskCfg.bg} ${taskCfg.color} ${taskIsActive ? "animate-pulse" : ""}`}>
                                      {taskCfg.label}
                                    </span>
                                    {!taskIsActive && (
                                      <span className="text-[9px] text-white/25">
                                        {formatTime(task.completed_at || task.created_at)}
                                      </span>
                                    )}
                                    {taskIsActive && subTaskProgress > 0 && (
                                      <span className="text-[9px] text-blue-400/80">
                                        AI Generating
                                      </span>
                                    )}
                                  </div>
                                  {taskPromptText && (
                                    <SubTaskPromptPreview
                                      prompt={taskPromptText}
                                      copied={copiedPromptId === taskPromptKey}
                                      onCopy={(e) => handleCopyPrompt(e, taskPromptText, taskPromptKey)}
                                    />
                                  )}
                                  {taskIsActive && (
                                    <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                                      <div
                                        className="h-full rounded-full transition-all duration-1000 ease-out"
                                        style={{
                                          width: `${Math.min(Math.max(subTaskProgress, 3), 100)}%`,
                                          background: "linear-gradient(90deg, #6BBDFC, #D24DDA)",
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
            {/* Load More Button */}
            {(hasMore || agentHasMore) && (
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="w-full mt-3 py-2 flex items-center justify-center gap-1.5 text-xs
                         text-white/50 hover:text-white/80 bg-white/4 hover:bg-white/8
                         rounded-lg transition-all disabled:opacity-50"
              >
                {loadingMore ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
                    加载中...
                  </>
                ) : (
                  "查看更多"
                )}
              </button>
            )}
            {!hasMore && !agentHasMore && archivedThrough && (
              <p className="mt-3 py-2 text-center text-xs text-white/40">
                {archivedThrough} 及之前的生成记录已归档，如需查看请联系管理员恢复
              </p>
            )}
          </>
        )}
      </div>

      {/* Image Preview Modal — portal 到 body，避免抽屉 transform 导致 fixed 仅覆盖窄栏 */}
      {previewTask && typeof document !== "undefined" && createPortal(
        <ImagePreviewModal
          task={previewTask}
          groupId={previewGroupId || undefined}
          onClose={() => { setPreviewTask(null); setPreviewGroupId(null); }}
          workflowOptions={workflowOptions}
        />,
        document.body,
      )}

      {detailGroup && (
        <HistoryRecordModal
          group={detailGroup}
          open={detailModalOpen}
          onClose={closeHistoryRecordModal}
          onAfterClose={finishHistoryRecordModalClose}
          onRegenerate={(payload) => {
            closeHistoryRecordModal();
            onRegenerateFromHistory?.(payload);
          }}
        />
      )}

    </div>
  );
}
